/* =======================================================================
   april. website script  (assets/april.js)
   -----------------------------------------------------------------------
   Draws the redesigned april. pages inside the "april. page" section:
     - Home page
     - Product pages (lenses and lens solution)
     - Shop by Shade (Browns, Greys, Blues, Greens)
     - Bundle & Save builder
     - Virtual Try-On (live camera)
   and runs the april. header (menu, bag count).

   Order of this file:
     1. Shop data and settings (products, prices, photos, links)
     2. Small helpers (prices, icons, safe text)
     3. Bag (add to bag, bag count, messages)
     4. Page parts and pages (cards, rows, home, product, shade, bundle)
     5. Virtual try-on (camera, face tracking, lens drawing)
     6. Clicks, scrolling rows and sliders
     7. Start-up: draw the page and the header
   ======================================================================= */
(function () {
  if (window.APRIL) return;


  /* ---------- 1. Shop data and settings ---------- */

  // The four shade families and how their names are shown.
  const FAM = { browns: 'Browns', blues: 'Blues', greens: 'Greens', greys: 'Greys' };
  // Product data written into the page by Shopify (sections/april-page.liquid).
  const D = window.APRIL_DATA || { products: [] };
  // Maps a product's Shopify "type" (Browns, Blues...) to its shade family.
  const FMAPF = { Browns: 'browns', Blues: 'blues', Greens: 'greens', Greys: 'greys' };
  // Builds a link to a picture in Shopify Content > Files.
  const FILE = (n) => '/cdn/shop/files/' + n;
  // Asks Shopify for a smaller copy of a picture (e.g. 500px wide) so pages load faster.
  const SZ = (u, w) => (u || '').replace(/width=\d+/, 'width=' + w);
  // Faster pages on phones: large images get a srcset so a phone downloads a phone-sized copy
  // (same photo and quality), while desktop still gets the original size. Images after the first
  // few on a page also load only when the customer scrolls near them.
  const RSP_W = [360, 540, 720, 960, 1200, 1600, 2000];
  // When code swaps a photo, drop the srcset so the new photo shows.
  const noSet = (el) => {
    if (el && el.hasAttribute('srcset')) {
      el.removeAttribute('srcset');
      el.removeAttribute('sizes');
    }
    return el;
  };
  const rsp = (html) => {
    let k = 0;
    return html.replace(/<img\b[^>]*>/g, (tag) => {
      k++;
      let out = tag;
      if (k > 3 && !/\sloading=/.test(out)) out = out.replace('<img', '<img loading="lazy"');
      if (!/\sdecoding=/.test(out)) out = out.replace('<img', '<img decoding="async"');
      const m = out.match(/\ssrc="([^"]*[?&]width=(\d+)[^"]*)"/);
      if (!m || /\ssrcset=/.test(out)) return out;
      const w = +m[2];
      if (w < 600) return out;
      const set = RSP_W.filter((x) => x < w).concat(w).map((x) => SZ(m[1], x) + ' ' + x + 'w').join(', ');
      return out.replace('<img', `<img srcset="${set}" sizes="(max-width: 749px) 75vw, ${w}px"`);
    });
  };
  // Lens photo (the round lens on its own) for each shade, from Content > Files.
  const DISC = {
    'aqua-sky-blue': 'Aqua_Sky_Blue_5b5e8904-3fd7-4dcd-addc-67e77dc3f6a0.png',
    'champagne-ash-grey': 'Champagne_Ash_Grey_9a516389-1614-4061-9cf2-bc2dd5df0a7e.png',
    'cream-latte-brown': 'Creme_Latte_Brown.png',
    'deep-charcoal-grey': 'Deep_Charcoal_Grey_b69f8eee-5526-4f9e-b074-31420687ba62.png',
    'deep-forest-green': 'Deep_Forest_Green_c5923128-93f0-4c30-95a8-8c12420b55aa.png',
    'emerald-gold-green': 'Emerald_Gold_Green_a82ce66e-5538-4782-86f8-97b0dc10322d.png',
    'golden-caramel-brown': 'Golden_Caramel_Brown_b7c949bd-1fff-498d-b61b-9d899043b99d.png',
    'ice-crystal-blue': 'Ice_Crystal_Blue_049f7881-b88d-42df-99b2-49fdacc244fa.png',
    'olive-mist-green': 'Olive_Mist_Green_53701584-9e4d-4dfd-a15a-6d98b27aff2b.png',
    'pearl-silver-grey': 'Pearl_Silver_Grey_4675708a-3b60-4743-b5c6-36ab34ab7217.png',
    'rich-chestnut-brown': 'Rich_Chestnut_Brown_2b40531d-8d40-4108-a82a-6abe237d3064.png',
    'royal-sapphire-blue': 'Royal_Sapphire_Blue_3bec6e1b-8b20-4fa1-8850-8136ce314934.png',
    'sage-hazel-green': 'Sage_Hazel_Green_8911350f-9f75-448d-b0cb-8071c10d0bf5.png',
    'smoky-ocean-blue': 'Smoky_Ocean_Blue_40f671c1-6137-4d29-8d9b-309e4504bf8b.png',
    'soft-cloud-grey': 'Soft_Cloud_Grey_555015b4-23a0-46ef-aa25-8cb9c509d61e.png',
    'soft-honey-hazel': 'Soft_Honey_Hazel_9c7bafb7-164b-4095-b701-43baa578ab05.png',
    'soft-mint-green': 'Soft_Mint_Green_37d3214b-7e25-4340-b029-2350b8f206aa.png',
    'steel-ice-blue': 'Steel_Ice_Blue_0b9571d9-2617-4ccb-b0fc-116737380894.png',
    'steel-mist-grey': 'Steel_Mist_Grey_e9635a7a-23b2-4118-a838-0e34fe975664.png',
    'velvet-mocha-brown': 'Velvet_Mocha_Brown_82b963b6-7639-4406-be1e-954c3553d9d4.png',
  };
  // "After" photo for the before/after slider on each product page, from Content > Files.
  const BAF = {
    'soft-honey-hazel': 'Soft_Honey_Hazel_Eyes.png',
    'golden-caramel-brown': 'Golden_Caramel_Brown_Eyes.png',
    'cream-latte-brown': 'Creme_Latte_Brown_Eyes.png',
    'velvet-mocha-brown': 'Velvet_Mocha_Brown_Eyes.png',
    'rich-chestnut-brown': 'Rich_Chesnut_Brown_Eyes.png',
    'steel-ice-blue': 'April._Edit_2026-09-26_1x_1_5090b654-7419-4d16-9f65-dd5a9ef08c53.png',
    'aqua-sky-blue': 'Aqua_Sky_Blue_Eyes.png',
    'smoky-ocean-blue': 'Smokey_Ocean_Blue_Eyes.png',
    'royal-sapphire-blue': 'Royal_Sapphire_Blue_Eyes.png',
    'ice-crystal-blue': 'Ice_Crystal_Blue_Eyes.png',
    'deep-forest-green': 'deep_Forest_Green_Eyes.png',
    'soft-mint-green': 'Soft_Mint_Green_Eyes.png',
    'olive-mist-green': 'Olive_Mist_Green_Eyes.png',
    'sage-hazel-green': 'Sage_Hazel_Green_Eyes.png',
    'emerald-gold-green': 'Emerlad_Gold_Green_Eyes.png',
    'soft-cloud-grey': 'Soft_Cloud_Grey_Eyes.png',
    'steel-mist-grey': 'Steel_Mist_Grey_Eyes.png',
    'deep-charcoal-grey': 'Deep_Charcoal_Grey_Eyes.png',
    'champagne-ash-grey': 'Champagne_Ash_Grey_Eyes.png',
    'pearl-silver-grey': 'Pearl_Silver_Grey_Eyes.png',
  };
  // All coloured lenses, built from the Shopify data: name, family, price, photos.
  const LENS = D.products
    .filter((p) => FMAPF[p.t] && DISC[p.h])
    .map((p) => ({
      h: p.h,
      n: p.n,
      f: FMAPF[p.t],
      price: p.price,
      was: p.was || p.price,
      v: p.v,
      av: p.av,
      img: p.imgs[0] || '',
      alt: p.imgs[1] || '',
      more: p.imgs.slice(2),
      disc: (p.imgs[1] || p.imgs[0] || '').replace(/width=\d+/, 'width=300'),
      img2: p.imgs[1] || p.imgs[0] || '',
    }));
  // The lens solution product (the only product without a shade family).
  const solP = D.products.find((p) => !FMAPF[p.t]) || { imgs: [] };
  // Lens solution details used on the home page and its product page.
  const SOL = {
    h: solP.h || '',
    n: solP.n || 'Lens Solution',
    price: solP.price || 490,
    was: solP.was || 999,
    img: solP.imgs[0] || '',
    alt: '',
    v: solP.v,
  };
  // Quick lookup: product handle -> lens details.
  const BY = Object.fromEntries(LENS.map((p) => [p.h, p]));
  // Web addresses used for links (product, shade family, bundle builder, try-on).
  const U = {
    home: '/',
    shop: '/collections/shop',
    p: (h) => '/products/' + h,
    fam: (f) => '/collections/' + f,
    bundle: (n) => '/pages/bundle-save' + (n ? '?n=' + n : ''),
    tryon: (h) => '/pages/virtual-try-on' + (h ? '?shade=' + h : ''),
  };
  // Photo used in the home page try-on section.
  // TEST ONLY: open any page with ?model=new to see the new home try-on photo (?model=old switches back).
  const TEST_MODEL = (() => {
    try {
      if (/[?&]model=new/.test(location.search)) sessionStorage.setItem('aprModel', '1');
      if (/[?&]model=old/.test(location.search)) sessionStorage.removeItem('aprModel');
      return sessionStorage.getItem('aprModel') === '1';
    } catch (e) {
      return /[?&]model=new/.test(location.search);
    }
  })();
  const HOMEIMG = FILE(TEST_MODEL ? 'april-home-model-test.jpg' : 'Brown_Lenses_739efbac-05d1-4222-b900-508709be6bd7.png') + '?width=700';
  // Shade family tile photos (Browns, Blues, Greens, Greys).
  const SHADEIMG = (f) =>
    FILE({ browns: 'Browns.png', blues: 'Blues.png', greens: 'Greens.png', greys: 'Greys.png' }[f]) +
    '?width=600';
  // "After" photo for a shade on the product page slider.
  const BAIMG = (h) => FILE(BAF[h] || '1._Master_Eye.png') + '?width=1400';
  // "Before" photo (natural dark eyes) for the product page slider.
  const BEFOREIMG = FILE('1._Master_Eye.png') + '?width=1400';
  // Order of lenses in the home page "Shop All" row.
  const SHOP_ALL_RAW = [
    'royal-sapphire-blue',
    'emerald-gold-green',
    'steel-mist-grey',
    'velvet-mocha-brown',
    'deep-charcoal-grey',
    'ice-crystal-blue',
    'rich-chestnut-brown',
    'sage-hazel-green',
  ];
  // Order of lenses in the home page "Featured Products" row.
  const FEATURED_RAW = [
    'soft-cloud-grey',
    'deep-forest-green',
    'rich-chestnut-brown',
    'aqua-sky-blue',
    'royal-sapphire-blue',
    'deep-charcoal-grey',
    'emerald-gold-green',
    'velvet-mocha-brown',
  ];
  // Keeps only lenses that exist in the shop (so a removed product never breaks the page).
  const SHOP_ALL = SHOP_ALL_RAW.filter((h) => BY[h]),
    FEATURED = FEATURED_RAW.filter((h) => BY[h]);
  // Bundle & Save offers: pick 2, 3 or 4 pairs, price and saving.
  const TIERS = [
    { n: 2, price: 4600, save: 600, img: FILE('Pick_your_2.png') + '?width=900' },
    { n: 3, price: 6300, save: 1500, img: FILE('Pick_your_3.png') + '?width=900', loved: true },
    { n: 4, price: 8000, save: 2400, img: FILE('Pick_your_4.png') + '?width=900' },
  ];
    /* ---------- 2. Small helpers ---------- */

  // Formats a price like "Rs. 2,600.00".
  const money = (n) => 'Rs. ' + n.toLocaleString('en-US') + '.00';
  // Formats a price like "Rs.2,600.00" (no space).
  const moneyC = (n) => 'Rs.' + n.toLocaleString('en-US') + '.00';
  // Makes text safe to put inside the page (stops names breaking the HTML).
  const esc = (s) =>
    String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  // Small arrow icon.
  const ARROW =
    '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
  // Shopping bag icon.
  const BAG =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linejoin="round"><path d="M5 8h14l-1 12H6L5 8Z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg>';


  /* ---------- 3. Bag ---------- */

  // Timer for the pop-up message (toast).
  let tt;
  // Shows a short pop-up message, e.g. "Added to bag".
  function toast(m, link) {
    const t = document.getElementById('aprToast');
    if (!t) return;
    t.innerHTML = esc(m) + (link ? ' <a href="/cart">View bag</a>' : '');
    t.classList.add('show');
    clearTimeout(tt);
    tt = setTimeout(() => t.classList.remove('show'), 3200);
  }
  // Reads the bag from Shopify and updates the number on the bag icon.
  async function setCount() {
    try {
      const c = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      document.querySelectorAll('[data-apr-count]').forEach((el) => (el.textContent = c.item_count));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (e) {}
  }
  // Adds lenses to the Shopify bag, then opens the bag or shows a message.
  async function cartAdd(items, go) {
    items = items.filter((i) => i.id);
    if (!items.length) {
      toast('This shade is unavailable right now.');
      return;
    }
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const j = await r.json();
      if (!r.ok) throw j;
      if (go) {
        location.href = '/cart';
        return;
      }
      await setCount();
      toast('Added to bag', true);
    } catch (e) {
      toast((e && (e.description || e.message)) || 'Could not add to bag. Please try again.');
    }
  }
  // Groups the same lens picked twice into one bag line with quantity 2.
  const aggItems = (hs) => {
    const m = {};
    hs.forEach((h) => {
      const v = BY[h] && BY[h].v;
      if (v) m[v] = (m[v] || 0) + 1;
    });
    return Object.entries(m).map(([id, quantity]) => ({ id: +id, quantity }));
  };

  /* ---------- 4. Page parts and pages ---------- */

  // One product card: photo (second photo on hover), name and price.
  const pcard = (p, sale) =>
    `<a class="pc" href="${U.p(p.h)}"><div class="im">${sale ? '<span class="sale">Sale</span>' : ''}<img class="a" src="${SZ(p.img, 500)}" alt="${esc(p.n)}" loading="lazy" width="500" height="500">${p.alt ? `<img class="b" src="${SZ(p.alt, 400)}" alt="${esc(p.n)} lens" loading="lazy" width="400" height="400">` : ''}</div><div class="nm">${esc(p.n)}</div><div class="pr">${sale ? moneyC(p.price) + '<s>' + moneyC(p.was) + '</s>' : money(p.price)}</div></a>`;
  // A sideways-scrolling row of product cards with arrows and dots.
  const rail = (id, list) =>
    `<div class="rail-w"><button class="ra l" data-dir="-1" aria-label="Scroll left"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="m15 6-6 6 6 6"/></svg></button><div class="rail" id="${id}">${list.map((h) => pcard(BY[h])).join('')}</div><button class="ra r" data-dir="1" aria-label="Scroll right"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="m9 6 6 6-6 6"/></svg></button></div><div class="dots" data-rail="${id}"><button aria-label="Slide 1" class="on"></button><button aria-label="Slide 2"></button><button aria-label="Slide 3"></button></div><div class="viewall"><a href="${U.shop}">View all ${ARROW}</a></div>`;
  // Shades the home try-on section cycles through.
  const HOME_SHADES = [
    'royal-sapphire-blue',
    'emerald-gold-green',
    'golden-caramel-brown',
    'pearl-silver-grey',
    'aqua-sky-blue',
    'sage-hazel-green',
    'velvet-mocha-brown',
    'steel-mist-grey',
  ];
  // Where the eyes are in the home try-on photo (so the lens colour lands on the iris).
  const HOME_EYES = TEST_MODEL ? [{"cx":243.1,"cy":274.9,"r":20.95,"poly":[[202.8,272.7],[210.9,263.0],[227.0,256.5],[243.1,253.3],[259.2,256.5],[275.4,267.8],[286.6,283.9],[281.8,287.2],[265.7,290.4],[243.1,295.9],[223.8,294.6],[209.3,288.8]]},{"cx":449.4,"cy":255.9,"r":21.59,"poly":[[391.4,275.9],[404.3,253.3],[423.6,242.0],[446.2,237.8],[468.7,240.4],[488.1,248.5],[497.7,253.3],[484.8,264.6],[468.7,271.0],[446.2,274.3],[423.6,274.3],[404.3,274.3]]}] : [
    {
      cx: 280.6,
      cy: 269.5,
      r: 12.77,
      poly: [
        [252.9, 270.9],
        [257.5, 275.4],
        [262.7, 278.2],
        [270.3, 280.5],
        [281.7, 281.4],
        [292.9, 280.7],
        [303.7, 279.2],
        [310.7, 279.0],
        [314.0, 278.1],
        [309.8, 273.6],
        [300.8, 266.3],
        [288.9, 260.6],
        [277.3, 259.2],
        [266.0, 261.4],
        [259.2, 265.3],
        [255.6, 268.3],
      ],
    },
    {
      cx: 419.0,
      cy: 271.1,
      r: 12.87,
      poly: [
        [449.7, 271.0],
        [445.1, 275.3],
        [440.0, 278.2],
        [432.3, 280.5],
        [421.0, 281.9],
        [409.7, 281.4],
        [398.8, 280.3],
        [391.7, 280.1],
        [388.0, 279.3],
        [392.1, 274.7],
        [400.9, 267.4],
        [412.6, 261.6],
        [424.7, 259.9],
        [436.3, 261.8],
        [443.2, 265.4],
        [447.2, 268.4],
      ],
    },
  ];
  // Home page try-on section: model photo that changes lens colour.
  const tryBlock =
    () => `<div class="tryon tband"><div class="stage hstage" id="hStage"><img class="m" src="${HOMEIMG}" alt="Model wearing april. lenses" width="700" height="700"><canvas class="hc" id="hCv" width="700" height="700" aria-hidden="true"></canvas>
  <a class="hchip" id="hChip" href="${U.tryon()}" data-tryshade="royal-sapphire-blue"><img src="${BY['royal-sapphire-blue'].disc}" alt="${esc(BY['royal-sapphire-blue'].n)} lens"><span><b>Royal Sapphire Blue</b>Rs.2,600</span></a>
  <div class="hsw" role="group" aria-label="Choose a shade">${HOME_SHADES.map((h, i) => `<button class="${i ? '' : 'on'}" data-hshade="${h}" aria-label="${esc(BY[h].n)}" aria-pressed="${!i}"><img src="${BY[h].disc}" alt="${esc(BY[h].n)} lens"></button>`).join('')}</div></div>
  <div><h2>Try It On.<br>Before You Put It On.</h2><p>See how every april. shade looks on your eyes with our Virtual Try-On. Find your favourites, compare shades, and choose with confidence.</p><a class="btn alt" id="hTryBtn" href="${U.tryon()}" data-tryshade="royal-sapphire-blue">Virtual Try-On</a></div></div>`;
  // State of the home try-on animation (current shade, timers).
  const HT = { i: 0, img: null, timer: 0, pause: 0, front: 0, io: null, visible: true };
  // Drawing surfaces used to fade from one lens colour to the next.
  const hNew = document.createElement('canvas'),
    hOld = document.createElement('canvas');
  hNew.width = hNew.height = hOld.width = hOld.height = 700;
  // Draws the model photo wearing the next shade.
  async function hRender(h) {
    const st = document.getElementById('hStage'),
      cv = document.getElementById('hCv');
    if (!st || !cv) return;
    if (!HT.img) HT.img = await loadImg(HOMEIMG);
    const tex = VT.tex[h] || (await lensTex(h));
    const nx = hNew.getContext('2d', { willReadFrequently: true });
    nx.globalCompositeOperation = 'source-over';
    nx.globalAlpha = 1;
    nx.drawImage(HT.img, 0, 0, 700, 700);
    renderEyes(nx, HT.img, HOME_EYES, tex, { k: 1, c: [1, 1, 1] }, 0.9, 1);
    const x = cv.getContext('2d');
    // only the eye areas change; the rest of the photo is never redrawn mid-fade
    const boxes = HOME_EYES.map((e) => {
      const r = e.r * 1.6;
      return [Math.floor(e.cx - r * 1.6), Math.floor(e.cy - r), Math.ceil(r * 3.2), Math.ceil(r * 2)];
    });
    cancelAnimationFrame(HT.raf);
    if (!HT.drawn || matchMedia('(prefers-reduced-motion:reduce)').matches) {
      x.drawImage(hNew, 0, 0);
      HT.drawn = true;
    } else {
      hOld.getContext('2d').drawImage(cv, 0, 0);
      const t0 = performance.now(),
        D = 550;
      const step = (now) => {
        const t = Math.min(1, (now - t0) / D),
          k = t * t * (3 - 2 * t);
        for (const [bx, by, bw, bh] of boxes) {
          x.globalAlpha = 1;
          x.drawImage(hOld, bx, by, bw, bh, bx, by, bw, bh);
          x.globalAlpha = k;
          x.drawImage(hNew, bx, by, bw, bh, bx, by, bw, bh);
        }
        x.globalAlpha = 1;
        if (t < 1) HT.raf = requestAnimationFrame(step);
      };
      HT.raf = requestAnimationFrame(step);
    }
    const p = BY[h],
      chip = document.getElementById('hChip');
    chip.dataset.tryshade = h;
    noSet(chip.querySelector('img')).src = p.disc;
    chip.querySelector('b').textContent = p.n;
    document.getElementById('hTryBtn').dataset.tryshade = h;
    st.querySelectorAll('[data-hshade]').forEach((b) => {
      const on = b.dataset.hshade === h;
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', on);
    });
  }
  // Starts the home try-on colour cycle (pauses when off screen).
  function hStart() {
    clearInterval(HT.timer);
    cancelAnimationFrame(HT.raf);
    HT.img = null;
    HT.drawn = false;
    HT.i = 0;
    HT.pause = 0;
    const st = document.getElementById('hStage');
    if (!st) return;
    hRender(HOME_SHADES[0]);
    if (matchMedia('(prefers-reduced-motion:reduce)').matches) return;
    if (HT.io) HT.io.disconnect();
    HT.io = new IntersectionObserver(
      (es) => {
        HT.visible = es[0].isIntersecting;
      },
      { threshold: 0.2 },
    );
    HT.io.observe(st);
    HT.timer = setInterval(() => {
      if (!document.getElementById('hStage')) {
        clearInterval(HT.timer);
        return;
      }
      if (!HT.visible || document.hidden || Date.now() < HT.pause) return;
      HT.i = (HT.i + 1) % HOME_SHADES.length;
      hRender(HOME_SHADES[HT.i]);
    }, 2600);
    st.addEventListener('click', (e) => {
      const b = e.target.closest('[data-hshade]');
      if (!b) return;
      HT.i = HOME_SHADES.indexOf(b.dataset.hshade);
      HT.pause = Date.now() + 9000;
      hRender(b.dataset.hshade);
    });
  }
  // Home page Bundle & Save cards.
  const bundleBlock = () =>
    `<div class="bundles">${TIERS.map(
      (
        t,
      ) => `<div class="bcard ${t.loved ? 'loved' : ''}"><div class="im"><img src="${t.img}" alt="${t.n} boxes of april. lenses" loading="lazy" width="700" height="700">${t.loved ? '<span class="tag loved">Most Loved</span>' : ''}<span class="tag save">Save Rs.${t.save}</span></div>
  <div class="bd"><h3>Pick your ${t.n}</h3><div class="p">Rs.${t.price}</div><div class="pp">(Rs.${t.price / t.n} a pair)</div><button class="btn ${t.loved ? '' : 'line'}" data-bundle="${t.n}">Choose Colours</button></div></div>`,
    ).join('')}</div>`;
  // Home page "Real eyes. Real reviews." creator videos. Each video is a "Creator video" block
  // added in Online Store > Customize (home page > april. page section). Until videos are added,
  // the unpublished theme shows four empty tiles; the live site hides the section.
  const vidBlock = () => {
    const list = (D.videos || []).filter((v) => v.v);
    const preview = !list.length && window.Shopify && Shopify.theme && Shopify.theme.role !== 'main';
    if (!list.length && !preview) return '';
    const card = (v) => {
      const p = BY[v.s];
      return `<div class="vr-card"><div class="vr-v"><video src="${v.v}" ${v.i ? `poster="${v.i}"` : ''} muted playsinline loop preload="metadata" aria-label="${esc(v.c ? v.c + ' wearing ' : 'Creator wearing ')}${p ? esc(p.n) : 'april. lenses'}"></video>
      <button class="vr-snd" type="button" aria-label="Play with sound"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg></button>${v.c ? `<span class="vr-h">${esc(v.c)}</span>` : ''}${v.p ? '<span class="vr-pt">Partner</span>' : ''}</div>
      ${p ? `<a class="vr-shade" href="${U.p(p.h)}"><img src="${p.disc}" alt="" width="40" height="40"><span><b>${esc(p.n)}</b>Shop this shade</span></a>` : ''}</div>`;
    };
    const empty = () =>
      '<div class="vr-card"><div class="vr-v vr-empty"><span>Creator video</span><small>Add in Customize</small></div></div>';
    return `<section class="blk vr-sec"><div class="wrap"><div class="vr-head"><div><p class="vr-ey">As seen on you</p><h2 class="sec-t">Real eyes. Real reviews.</h2><p class="vr-sub">See april. shades on real eyes, in real light.</p></div>
  <div class="vr-arw"><button type="button" data-vr="-1" aria-label="Previous videos">←</button><button type="button" data-vr="1" aria-label="Next videos">→</button></div></div>
  <div class="vr-row" id="vrRow">${list.length ? list.map(card).join('') : [0, 1, 2, 3].map(empty).join('')}</div></div></section>`;
  };
  // Creator videos: play muted on hover (computer) or when in view (phone); the play button turns the sound on.
  function vidBind(app) {
    const row = app.querySelector('#vrRow');
    if (!row) return;
    const vids = [...row.querySelectorAll('video')];
    const hover = window.matchMedia('(hover:hover)').matches;
    const play = (v) => {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };
    if (hover)
      row.querySelectorAll('.vr-v').forEach((b) => {
        const v = b.querySelector('video');
        if (!v) return;
        b.addEventListener('mouseenter', () => play(v));
        b.addEventListener('mouseleave', () => {
          if (v.muted) v.pause();
        });
      });
    else if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (es) => es.forEach((e) => (e.isIntersecting ? play(e.target) : e.target.muted && e.target.pause())),
        { threshold: 0.6 },
      );
      vids.forEach((v) => io.observe(v));
    }
    row.addEventListener('click', (e) => {
      const b = e.target.closest('.vr-snd');
      if (!b) return;
      const v = b.parentElement.querySelector('video');
      const on = v.muted || v.paused;
      vids.forEach((o) => {
        o.muted = true;
        o.closest('.vr-v').classList.remove('snd');
        if (o !== v && !hover) o.pause();
      });
      if (on) {
        v.muted = false;
        v.currentTime = 0;
        play(v);
        b.closest('.vr-v').classList.add('snd');
        b.setAttribute('aria-label', 'Mute');
      } else {
        b.setAttribute('aria-label', 'Play with sound');
      }
    });
    app.querySelectorAll('[data-vr]').forEach((b) =>
      b.addEventListener('click', () => {
        const c = row.querySelector('.vr-card');
        row.scrollBy({ left: +b.dataset.vr * (c ? c.offsetWidth + 16 : 300) * 2, behavior: 'smooth' });
      }),
    );
  }
  // The home page, top to bottom: hero, Shop All, try-on, Shop by Shade, bundles, creator videos, Customer Love reviews, featured, lens solution, newsletter, journal.
  function pHome() {
    return `
<section class="hero-sec"><div class="wrap"><div class="hero">
  <picture><source media="(max-width:749px)" srcset="${FILE('banner_mobbb.png') + '?width=1000'}"><img src="${FILE('main_hero_image.png') + '?width=1600'}" srcset="${FILE('main_hero_image.png')}?width=1000 1000w, ${FILE('main_hero_image.png')}?width=1600 1600w, ${FILE('main_hero_image.png')}?width=2400 2400w" sizes="(min-width:1700px) 1600px, 100vw" alt="Model wearing Royal Sapphire Blue lenses" width="1280" height="683" fetchpriority="high"></picture>
  <a class="badge" href="${U.p('royal-sapphire-blue')}"><img src="${FILE('Product_On_Banner.png') + '?width=200'}" alt="Royal Sapphire Blue lens"><span><b>Royal Sapphire Blue</b>Rs.2600</span></a>
  <div class="hero-copy"><h1>Wear it. Love it. Own it.</h1><p>Coloured contact lenses for dark eyes made with Korean technology for lasting comfort and beautiful colour payoff.</p><a class="btn" href="${U.shop}">Find Your Shade</a></div>
</div></div></section>
<section class="blk"><div class="wrap"><h2 class="sec-t">Shop All</h2>${rail('railA', SHOP_ALL)}</div></section>
<section class="blk"><div class="wrap">${tryBlock()}</div></section>
<section class="blk"><div class="wrap"><h2 class="sec-t">Shop by Shade</h2><div class="shades">${['browns', 'blues', 'greens', 'greys'].map((f) => `<a class="shade" href="${U.fam(f)}"><img src="${SHADEIMG(f)}" alt="Model wearing ${FAM[f].toLowerCase()} lenses" loading="lazy" width="700" height="700"><span>Shop ${FAM[f]}</span></a>`).join('')}</div></div></section>
<section class="blk"><div class="wrap"><h2 class="sec-t">Bundle &amp; Save</h2>${bundleBlock()}</div></section>
${vidBlock()}
<section class="blk hrev is-off"><div class="wrap"><div class="rv-head"><h2 class="sec-t">Customer Love</h2></div><div class="hrev-jdgm"></div></div></section>
<section class="blk"><div class="wrap"><h2 class="sec-t">Featured Products</h2>${rail('railB', FEATURED)}</div></section>
<section class="blk"><div class="wrap"><div class="sol"><img src="${FILE('Bottle_Banner.png') + '?width=1800'}" alt="april. multi-purpose lens solution" loading="lazy" width="1424" height="946">
  <div class="cp"><h2>Clean. Rinse. Wear.</h2><p>Made for everyday lens care, our multi-purpose solution cleans, rinses and keeps your lenses fresh and ready to wear.</p><a class="btn" href="${U.p(SOL.h)}">Shop Lens Solution</a></div>
  <a class="mini" href="${U.p(SOL.h)}"><img src="${SZ(SOL.img, 300)}" alt="april. lens solution bottle"><span><b>Lens Solution</b><s>Rs.999</s> <b style="display:inline">Rs.490</b></span></a></div></div></section>
<section class="blk"><div class="wrap"><div class="sub"><img src="${FILE('Email_Banner_1.png') + '?width=1800'}" alt="Model wearing april. coloured contact lenses" loading="lazy"><div class="in"><h2>Subscribe for<br>exclusive deals</h2><p>Get first dibs on new shades, special drops and offers - straight to your inbox.</p>
  <form id="subForm" method="post" action="/contact#contact_form" accept-charset="UTF-8" novalidate><input type="hidden" name="form_type" value="customer"><input type="hidden" name="utf8" value="✓"><input type="hidden" name="contact[tags]" value="newsletter"><label for="subEmail" style="position:absolute;left:-9999px">Email</label><input id="subEmail" name="contact[email]" type="email" placeholder="Enter your email"><button type="submit" aria-label="Subscribe">→</button></form><div class="ok" id="subOk"></div></div></div></div></section>
${(D.journal || []).length ? `<section class="blk jm-sec"><div class="wrap"><div class="jmx"><div class="jmx-l"><p class="jmx-tag">The april. Journal</p><h2>Know your lenses.</h2><p class="jmx-p">Easy reads on picking your shade, wearing lenses and looking after them.</p><a class="jmx-all" href="/blogs/journal">Read the Journal →</a></div><div class="jmx-m"><div class="jmx-t">${[0, 1].map((k) => D.journal.map((a) => `<a class="jmx-c" href="${a.u}"${k ? ' aria-hidden="true" tabindex="-1"' : ""}><span class="jmx-im">${a.i ? `<img src="${a.i}" alt="${k ? "" : esc(a.alt)}" loading="lazy" width="400" height="400">` : ""}</span>${a.g ? `<span class="jmx-g">${esc(a.g)}</span>` : ""}<b>${esc(a.n)}</b></a>`).join("")).join("")}</div></div></div></div></section>` : ""}`;
  }

  // Quantity chosen on a product page.
  let qty = 1;
  // Product description from Shopify, shown in the "Details" drop-down.
  const DESC = () => {
    const t = document.getElementById('aprDesc');
    return t && t.innerHTML.trim()
      ? (/<details/i.test(t.innerHTML) ? `<div class="acc">${t.innerHTML}</div>` : `<div class="acc"><details open><summary>Details</summary><div class="ab">${t.innerHTML}</div></details></div>`)
      : '';
  };
  // Lens product page: photos, pack, Add to Bag, details, before/after + try-on, reviews, questions, shade line-up.
  function pProduct(p) {
    qty = 1;
    const t = document.getElementById('aprDesc');
    const acc = t && /<details/i.test(t.innerHTML) ? `<div class="mp-acc">${t.innerHTML}</div>` : DESC();
    const th = [[p.img2, 'disc'], [p.img, 'photo'], ...p.more.map((u) => [u, 'photo'])].filter((x, i, a) => x[0] && a.findIndex((y) => y[0] === x[0]) === i);
    const fam = LENS.filter((x) => x.f === p.f);
    const packs = [{ n: 1, price: p.price, save: 0 }, ...TIERS];
    const rs = (n) => 'Rs.' + n.toLocaleString('en-US');
    const wa = 'https://wa.me/923319576192?text=' + encodeURIComponent(`Hi april., I'd like to write a review for ${p.n}.`);
    const first = th[0] || [p.img, 'photo'];
    return `<section class="mp-top">
  <div class="mp-gal"><div class="mp-th">${th.map(([u, f], i) => `<button type="button" class="${i ? '' : 'on'}" data-mpsrc="${SZ(u, 1400)}" data-fit="${f}" aria-label="Photo ${i + 1}"><img src="${SZ(u, 160)}" alt="" width="160" height="160"></button>`).join('')}</div>
    <div class="mp-main${first[1] === 'disc' ? ' is-disc' : ''}" id="mpBox"><img id="mpMain" src="${SZ(first[0], 1400)}" alt="${esc(p.n)} coloured contact lens" width="1200" height="1200"></div></div>
  <div class="mp-info"><h1>${esc(p.n)}</h1>
    <p class="mp-desc" id="mpDesc">april. coloured contact lenses are made with soft Hydrogel material and 42% water content, combining colour, comfort and a natural-looking finish. Each shade is designed to blend with your natural iris, creating a visible colour effect that can vary depending on your natural eye colour and lighting.</p><button type="button" id="mpMore">Read more</button>
    <div class="mp-sel"><span>Power: 0.00 (zero power)</span></div>
    <div class="mp-price" id="mpPrice"${p.av ? '' : ' hidden'}><b id="mpTot">${rs(p.price)}</b><span id="mpSave"></span></div>
    <div class="mp-buy"><div class="mp-q"><button type="button" id="qm" aria-label="Decrease quantity">−</button><output id="qo">1</output><button type="button" id="qp" aria-label="Increase quantity">+</button></div><button type="button" class="mp-add" data-addq="${p.h}" ${p.av ? '' : 'disabled'}>${p.av ? 'Add to bag' : 'Sold out'}</button></div>
    <p class="mp-was">${p.was > p.price ? `<s>${rs(p.was)}</s> ` : ''}${rs(p.price)} per pair · Includes 60 ml lens solution + lens case</p>
    ${acc}</div></section>
<section class="mp-see2"><div class="see">
  <div class="see-t"><div class="ey">See the difference</div><h2>Same eyes.<br>${esc(p.n)}.</h2>
    <p>Natural dark brown eyes on the left. The same eyes wearing ${esc(p.n)} on the right. Drag to compare.</p>
    <div class="schip"><img src="${p.disc}" alt="${esc(p.n)} lens">${esc(p.n)} · ${moneyC(p.price)}</div>
    <a class="btn" href="${U.tryon(p.h)}" data-tryshade="${p.h}"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>Virtual Try-On</a></div>
  <div class="ba" id="ba" style="--p:50%">
    <div class="lay"><img class="fg" src="${BAIMG(p.h)}" alt="Eyes wearing ${esc(p.n)}" width="928" height="615"></div>
    <div class="lay bf"><img class="fg" src="${BEFOREIMG}" alt="The same eyes without lenses" width="928" height="615"></div>
    <span class="lb l">Before</span><span class="lb r">After</span>
    <span class="ln" aria-hidden="true"></span><span class="hd" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m8 7-5 5 5 5M16 7l5 5-5 5"/></svg></span>
    <span class="cap">Natural dark brown eyes</span>
    <input type="range" id="baR" min="0" max="100" value="50" aria-label="Drag to compare before and after ${esc(p.n)}">
  </div></div></section>
<section class="mp-rev" data-shade="${esc(p.n)}">
  <div class="mp-rh"><div><h2>Your pretty eyes, in your words.</h2><p>Real reviews of ${esc(p.n)}.</p></div><div class="mp-score"><b>0.0</b><span>★★★★★<small>Based on 0 reviews</small></span></div></div>
  <div class="mp-bars">${[5, 4, 3, 2, 1].map((s) => `<div><span>${s} ★</span><i></i><span>0</span></div>`).join('')}</div>
  <div class="mp-rbar"><span>Showing reviews for: ${esc(p.n)}</span></div>
  <div class="mp-cards" id="mpCards"></div>
  <div class="mp-empty"><b>No reviews yet.</b><br>Be the first to review ${esc(p.n)}.</div>
</section>
<section class="mp-faq" id="mpFaq"><h2>Your Questions, Answered.</h2>
  <details><summary>Do april. lenses have power?</summary><p>No. april. lenses are zero power, so they change your colour, not your vision.</p></details>
  <details><summary>Will the colour show on my dark brown eyes?</summary><p>Every april. shade is designed with dark eyes in mind. The final look can vary with your natural eye colour and lighting, so try it on with <a href="${U.tryon(p.h)}">Virtual Try-On</a> first.</p></details>
  <details><summary>What comes in the box?</summary><p>1 pair of april. coloured contact lenses, a 60 ml lens solution and a lens case.</p></details>
  <details><summary>How much is delivery?</summary><p>Delivery is free on all orders, with no minimum. Orders take 1–2 working days to process and 3–5 working days to arrive. Cash on delivery is available.</p></details>
  <details><summary>Can I return my lenses?</summary><p>For hygiene reasons lenses can't be returned unless they arrive damaged, defective or incorrect. See our <a href="/pages/refund-return-policy">return policy</a>.</p></details>
</section>
<section class="mp-line">${LENS.filter((x) => x.h !== p.h).map((x) => `<a href="${U.p(x.h)}"><span><img src="${SZ(x.img, 400)}" alt="${esc(x.n)}" loading="lazy" width="400" height="400"></span>${esc(x.n)}</a>`).join('')}</section>
<section class="mp-dark mp-spec"><div class="mp-spec-lens"><img src="${SZ(p.disc, 400)}" alt="${esc(p.n)} lens" loading="lazy" width="300" height="300"></div><h2>${esc(p.n)}, up close.</h2><p>The details behind the colour.</p><div class="mp-spec-g"><div class="wait" data-spec="Diameter"><b></b><span>Diameter</span></div><div class="wait" data-spec="Base Curve"><b></b><span>Base curve</span></div><div><b>42%</b><span>Water content</span></div><div><b>Hydrogel</b><span>Material</span></div><div><b>0.00</b><span>Zero power</span></div></div></section>`;
  }
  // Product page: shows the total for the chosen number of pairs (bundle prices for 2, 3 and 4+ pairs).
  function mpSync(app) {
    const t = app.querySelector('#mpTot');
    if (!t) return;
    const h = app.querySelector('[data-addq]'),
      p = h && BY[h.dataset.addq];
    if (!p) return;
    const tier = TIERS.find((x) => x.n === qty),
      top = TIERS[TIERS.length - 1],
      full = p.price * qty,
      tot = qty === 1 ? p.price : tier ? tier.price : Math.round((top.price / top.n) * qty),
      rs = (n) => 'Rs.' + n.toLocaleString('en-US');
    t.textContent = rs(tot);
    app.querySelector('#mpSave').textContent = full > tot ? 'for ' + qty + ' pairs · you save ' + rs(full - tot) : qty > 1 ? 'for ' + qty + ' pairs' : '';
  }
  // Product page: puts Judge.me's review list (added in Customize as an app block) into "Loved by our customers".
  // Customer Love row slides on its own every few seconds; pauses while the customer is touching or hovering it.
  function hrcSlide(row) {
    if (!row || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let paused = false;
    ['mouseenter', 'touchstart', 'focusin'].forEach((e) => row.addEventListener(e, () => (paused = true), { passive: true }));
    ['mouseleave', 'touchend', 'focusout'].forEach((e) => row.addEventListener(e, () => (paused = false), { passive: true }));
    setInterval(() => {
      if (paused || document.hidden || row.scrollWidth <= row.clientWidth + 4) return;
      const c = row.querySelector('.hrc-card'), step = c ? c.offsetWidth + 16 : 300;
      if (row.scrollLeft + row.clientWidth >= row.scrollWidth - 4) row.scrollTo({ left: 0, behavior: 'smooth' });
      else row.scrollBy({ left: step, behavior: 'smooth' });
    }, 3500);
  }
  // Home page Featured Products: same lenses, re-ordered by Shopify's "Best selling" order (top seller first).
  // Reads the order from the All products collection sorted by best-selling; cached for 1 hour per visit.
  function featuredBest(app) {
    const row = app.querySelector('#railB');
    if (!row) return;
    const hOf = (c) => ((c.getAttribute('href') || '').split('/products/')[1] || '').split(/[?#/]/)[0];
    const apply = (order) => {
      if (!order || !order.length) return;
      const cards = [...row.children].filter((c) => c.classList.contains('pc'));
      const rank = (c) => { const i = order.indexOf(hOf(c)); return i < 0 ? 999 : i; };
      cards.map((c, i) => [c, rank(c), i]).sort((a, b) => a[1] - b[1] || a[2] - b[2]).forEach(([c]) => row.appendChild(c));
      row.scrollLeft = 0;
      row.dispatchEvent(new Event('scroll'));
    };
    let cached = null;
    try { cached = JSON.parse(sessionStorage.getItem('aprBest') || 'null'); } catch (e) {}
    if (cached && Date.now() - cached.t < 3600000) return apply(cached.h);
    const read = (html) => [...new Set([...html.matchAll(/\/products\/([a-z0-9-]+)/g)].map((m) => m[1]))];
    const base = '/collections/all?sort_by=best-selling';
    fetch(base + '&section_id=template--24198604488923__main')
      .then((r) => (r.ok ? r.text() : ''))
      .then((t) => { const h = read(t); return h.length ? h : fetch(base).then((r) => r.text()).then(read); })
      .then((h) => {
        try { sessionStorage.setItem('aprBest', JSON.stringify({ t: Date.now(), h })); } catch (e) {}
        apply(h);
      })
      .catch(() => {});
  }
  // Home page Customer Love: april. cards built from the Judge.me reviews you feature (heart icon). Hidden until there is at least one.
  function homeReviews(root, app) {
    const sec = app.querySelector('.hrev'), box = sec && sec.querySelector('.hrev-jdgm'), apps = root.querySelector('#aprApps');
    if (!box || !apps) return;
    const build = () => {
      const seen = new Set(), list = [];
      apps.querySelectorAll('.jdgm-carousel-item').forEach((c) => {
        const id = c.dataset.reviewId;
        if (!id || seen.has(id)) return;
        seen.add(id);
        const t = c.querySelector('.jdgm-text'), a = c.querySelector('.jdgm-product-name a'), nm = c.querySelector('.jdgm-reviewer-name');
        const h = a ? (a.getAttribute('href') || '').split('/products/')[1] || '' : '';
        const q = t ? t.textContent.trim() : '';
        if (q) list.push({ q, n: nm ? nm.textContent.trim() : '', s: c.querySelectorAll('.jdgm-star.jdgm--on').length, h: h.split(/[?#]/)[0], p: a ? a.textContent.trim() : '' });
      });
      if (!list.length) return false;
      box.innerHTML = '<div class="hrc">' + list.map((r) => {
        const b = BY[r.h];
        return `<a class="hrc-card" href="${b ? U.p(r.h) : '/pages/reviews'}"><div class="hrc-top"><span class="hrc-shade">${b ? `<img src="${b.disc}" alt="" width="18" height="18">` : ''}${esc(b ? b.n : r.p)}</span><span class="hrc-st" aria-label="${r.s} out of 5 stars">${'★'.repeat(r.s)}</span></div><q>${esc(r.q)}</q><span class="hrc-who">${esc(r.n)}</span></a>`;
      }).join('') + '</div>';
      sec.classList.remove('is-off');
      hrcSlide(box.querySelector('.hrc'));
      return true;
    };
    if (build()) return;
    // Judge.me fills its carousel a moment after the page loads, so wait for it.
    let tm;
    const mo = new MutationObserver(() => {
      clearTimeout(tm);
      tm = setTimeout(() => { if (build()) mo.disconnect(); }, 300);
    });
    mo.observe(apps, { childList: true, subtree: true });
    setTimeout(() => mo.disconnect(), 15000);
  }
  // Product page reviews as april. cards (name, verified badge, date, stars, review, shade tag, photos), built from the Judge.me reviews for this shade.
  function mpCards(rv, src) {
    const out = rv.querySelector('#mpCards');
    if (!out) return;
    const MON = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const build = () => {
      const items = src.querySelectorAll('.jm-review-item');
      if (!items.length) return false;
      const list = [...items].map((it) => {
        const nm = it.querySelector('.jm-reviewer-info__name'), det = it.querySelector('.jm-reviewer-info__details');
        const dm = det ? det.textContent.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/) : null;
        const sr = it.querySelector('.jm-star-rating'), b = it.querySelector('.jm-review-content__body'), av = it.querySelector('.jm-reviewer-avatar img');
        return {
          name: nm ? nm.textContent.trim() : 'Customer',
          date: dm ? MON[+dm[1] - 1] + ' ' + dm[3] : '',
          s: Math.min(5, Math.max(1, (sr && parseInt(sr.getAttribute('aria-label'), 10)) || 5)),
          q: b ? b.textContent.trim() : '',
          av: av ? av.src : '',
          ver: !!it.querySelector('[class*="verified"]'),
          imgs: [...it.querySelectorAll('.jm-media-thumbnail-list__thumbnail img')].map((i) => i.src).filter(Boolean),
        };
      });
      const tag = rv.dataset.shade || '';
      out.innerHTML = list.map((r) => `<article class="mpc"><div class="mpc-h"><span class="mpc-av">${r.av ? `<img src="${r.av}" alt="">` : esc(r.name.charAt(0).toUpperCase())}</span><div><div class="mpc-n"><b>${esc(r.name)}</b>${r.ver ? '<span class="mpc-v">✓ Verified</span>' : ''}</div><span class="mpc-d">${r.date}</span></div></div><div class="mpc-st" aria-label="${r.s} out of 5 stars">${'★'.repeat(r.s)}${'☆'.repeat(5 - r.s)}</div>${r.q ? `<p class="mpc-q">${esc(r.q)}</p>` : ''}${tag ? `<div class="mpc-tags"><span>${esc(tag)}</span></div>` : ''}${r.imgs.length ? `<div class="mpc-ph">${r.imgs.map((u) => `<a href="${u}" target="_blank" rel="noopener"><img src="${u}" alt="Customer photo" loading="lazy"></a>`).join('')}</div>` : ''}</article>`).join('');
      const tot = list.length;
      let avg = list.reduce((a, r) => a + r.s, 0) / tot, count = tot;
      const hp = src.querySelectorAll('.jm-average-rating-display p');
      if (hp.length) {
        const a = parseFloat(hp[0].textContent), c = parseInt(hp[1] ? hp[1].textContent : '', 10);
        if (a) avg = a;
        if (c) count = c;
      }
      const sc = rv.querySelector('.mp-score');
      if (sc) {
        sc.querySelector('b').textContent = avg.toFixed(1);
        const sm = sc.querySelector('small');
        if (sm) sm.textContent = 'Based on ' + count + (count === 1 ? ' review' : ' reviews');
      }
      if (count === tot) {
        rv.querySelectorAll('.mp-bars>div').forEach((d, i) => {
          const n = list.filter((r) => r.s === 5 - i).length;
          d.querySelector('i').style.setProperty('--w', (n / tot) * 100 + '%');
          d.lastElementChild.textContent = n;
        });
      } else rv.classList.add('no-bars');
      rv.classList.add('has-rv');
      return true;
    };
    if (build()) return;
    let tm;
    const mo = new MutationObserver(() => {
      clearTimeout(tm);
      tm = setTimeout(() => { if (build()) mo.disconnect(); }, 300);
    });
    mo.observe(src, { childList: true, subtree: true });
    setTimeout(() => mo.disconnect(), 15000);
  }
  function mpReviews(root, app) {
    const apps = root.querySelector('#aprApps'),
      rv = app.querySelector('.mp-rev');
    if (!apps || !rv || !apps.querySelector('[class*="jdgm"]')) return;
    const box = document.createElement('div');
    box.className = 'mp-jdgm';
    while (apps.firstChild) box.appendChild(apps.firstChild);
    rv.appendChild(box);
    rv.classList.add('has-jdgm');
    mpCards(rv, box);
  }
  // Product page "up close" band: fills diameter and base curve from the shade's Product Specifications table.
  function mpSpec(app) {
    const fill = () => {
      const rows = {};
      app.querySelectorAll('table tr').forEach((tr) => {
        const c = tr.querySelectorAll('td');
        if (c.length === 2) rows[c[0].textContent.trim()] = c[1].textContent.trim();
      });
      let left = 0;
      app.querySelectorAll('.mp-spec [data-spec].wait').forEach((t) => {
        const v = rows[t.dataset.spec];
        if (v) {
          t.querySelector('b').textContent = v;
          t.classList.remove('wait');
        } else left++;
      });
      return left === 0;
    };
    if (fill()) return;
    const mo = new MutationObserver(() => { if (fill()) mo.disconnect(); });
    mo.observe(app, { childList: true, subtree: true });
    setTimeout(() => mo.disconnect(), 8000);
  }
  // Product page extras: pack drop-down, read more, one drop-down open at a time, line-up hover scroll.
  function mpBind(app) {
    const s = app.querySelector('#mpPack');
    if (s)
      s.addEventListener('change', () => {
        qty = +s.value || 1;
        app.querySelector('#qo').textContent = qty;
        mpSync(app);
      });
    app.addEventListener(
      'toggle',
      (e) => {
        const d = e.target;
        if (!d.open || d.tagName !== 'DETAILS') return;
        const g = d.closest('.mp-acc,.mp-faq');
        if (g) g.querySelectorAll('details').forEach((o) => o !== d && (o.open = false));
      },
      true,
    );
    const ln = app.querySelector('.mp-line');
    if (ln && window.matchMedia('(hover:hover)').matches) {
      let v = 0,
        raf = 0;
      const step = () => {
        if (!v) return (raf = 0);
        ln.scrollLeft += v;
        raf = requestAnimationFrame(step);
      };
      ln.addEventListener('mousemove', (e) => {
        const r = ln.getBoundingClientRect(),
          x = (e.clientX - r.left) / r.width;
        v = x > 0.8 ? Math.round((x - 0.8) * 45) + 2 : x < 0.2 && ln.scrollLeft > 0 ? -(Math.round((0.2 - x) * 45) + 2) : 0;
        if (v && !raf) raf = requestAnimationFrame(step);
      });
      ln.addEventListener('mouseleave', () => (v = 0));
    }
  }

  // Lens solution product page.
  function pSolution() {
    qty = 1;
    return `<div class="wrap"><div class="pdp">
  <div class="gal"><div class="main"><img src="${SOL.img}" alt="${esc(SOL.n)}" width="600" height="600"></div></div>
  <div class="pinfo"><h1>${esc(SOL.n)}</h1><div class="pr">${moneyC(SOL.price)}${SOL.was > SOL.price ? `<s>${moneyC(SOL.was)}</s>` : ''}</div>
    <div class="qty"><button id="qm" aria-label="Decrease quantity">−</button><output id="qo">1</output><button id="qp" aria-label="Increase quantity">+</button></div>
    <button class="btn addbag" data-addsol>${BAG}Add to Bag</button>${DESC()}</div></div></div>`;
  }

  // Short line under each shade family name.
  const BLURB = {
    browns: 'Warm, soft and close to your own colour.',
    blues: 'From icy to deep sapphire, made to show on dark eyes.',
    greens: 'Mint, olive, hazel and emerald.',
    greys: 'Cloud-soft to deep charcoal.',
  };
  // Shop by Shade page for one family (Browns, Greys...): tabs, product grid, bundle tip.
  function pFamily(f) {
    const list = LENS.filter((p) => p.f === f);
    return `<section class="blk sbs"><div class="wrap"><div class="crumbs"><a href="/">Home</a> / Shop by Shade / ${FAM[f]}</div>
    <p class="bh">Shop by Shade</p>
    <div class="shades stabs" role="tablist" aria-label="Shade families">${['browns', 'blues', 'greens', 'greys'].map((x) => `<a class="shade ${x === f ? 'on' : ''}" href="${U.fam(x)}" data-fam="${x}" role="tab" aria-selected="${x === f}"><img src="${SHADEIMG(x)}" alt="Model wearing ${FAM[x].toLowerCase()} lenses" width="700" height="700"><span>${x === f ? '' : '<i class="sp">Shop </i>'}${FAM[x]}</span></a>`).join('')}</div>
    <div class="fbar"><div><h1>${FAM[f]}</h1><p>${BLURB[f]}</p></div><div class="meta">${list.length} shades · ${list[0] ? moneyC(list[0].price) : ''} a pair</div></div>
    <div class="fprods" role="tabpanel">${list.map((p) => `<div class="pcw">${pcard(p, true)}<button class="btn line" data-add="${p.h}">Add to bag</button></div>`).join('')}</div>
    <div class="nudge"><span>Choosing more than one? Pick 3 for Rs.6300, that's Rs.2100 a pair.</span><a href="${U.bundle(3)}">Build a bundle</a></div>
  </div></section>`;
  }
  // What the customer has picked in the bundle builder.
  let bState = { n: 3, picks: [] };
  // One bundle option button (Pick 2 / 3 / 4).
  const tierBtn = (
    t,
  ) => `<button class="tier ${bState.n === t.n ? 'sel' : ''}" data-tier="${t.n}" aria-pressed="${bState.n === t.n}"><div class="im"><img src="${SZ(t.img, 300)}" alt="${t.n} boxes of april. lenses" width="700" height="700">${t.loved ? '<span class="tag loved">Most Loved</span>' : ''}<span class="tag save">Save Rs.${t.save}</span></div>
  <div class="bd"><h3>Pick your ${t.n}</h3><span class="radio" aria-hidden="true"></span><div class="p">Rs.${t.price}</div><div class="pp">(Rs.${t.price / t.n} a pair)</div><span class="svp">Save Rs.${t.save}</span></div></button>`;
  // Bundle & Save page: choose a bundle, tap shades to fill it, add to bag.
  const pBundle =
    () => `<section class="blk"><div class="wrap"><div class="crumbs"><a href="/">Home</a> / Bundle &amp; Save</div><h1 class="bh">Bundle &amp; Save</h1><p class="bsub">Choose your bundle, then tap the shades you want. Mix any colours.</p>
  <div class="tiers" role="group" aria-label="Choose a bundle">${TIERS.map(tierBtn).join('')}</div>
  <div class="builder"><div><div class="bchips" role="group" aria-label="Filter shades by colour">${['all', 'browns', 'blues', 'greens', 'greys'].map((f, i) => `<button type="button" class="${i ? '' : 'on'}" data-bfam="${f}" aria-pressed="${!i}">${f === 'all' ? 'All shades' : FAM[f]}</button>`).join('')}</div>${['browns', 'blues', 'greens', 'greys']
    .map(
      (f) =>
        `<div class="pfam" data-fam="${f}"><h3>${FAM[f]}</h3><div class="bpicks">${LENS.filter((p) => p.f === f)
          .map(
            (p) =>
              `<button class="bp" data-bpick="${p.h}" aria-label="Add ${esc(p.n)}"><img src="${SZ(p.img, 400)}" alt="${esc(p.n)}" loading="lazy" width="600" height="600"><span class="n">${esc(p.n)}</span><span class="plus" data-c="${p.h}" aria-hidden="true">+</span></button>`,
          )
          .join('')}</div></div>`,
    )
    .join('')}</div>
  <aside class="sum" id="sum" aria-live="polite"></aside></div></div></section><div class="mbar" id="mbar"></div>`;
  // Redraws the picked shades and the total in the bundle builder.
  function renderSlots() {
    const sum = document.getElementById('sum');
    if (!sum) return;
    const t = TIERS.find((x) => x.n === bState.n),
      n = t.n,
      left = n - bState.picks.length,
      full = !left;
    sum.innerHTML = `<h3>Pick your ${n}</h3><div class="hint">${full ? 'All set. Tap a shade to remove it.' : `Choose ${left} more shade${left > 1 ? 's' : ''}.`}</div>
   <div class="bslots">${Array.from({ length: n }, (_, i) => {
     const h = bState.picks[i];
     return h
       ? `<button class="bslot full" data-unpick="${i}"><img src="${BY[h].img}" alt=""><span>${esc(BY[h].n)}<small>Tap to remove</small></span></button>`
       : `<div class="bslot">Pair ${i + 1}</div>`;
   }).join('')}</div>
   <div class="row"><span>Regular price</span><s>Rs.${(LENS[0] ? LENS[0].price : 2600) * n}</s></div><div class="row"><span>You save</span><span class="sv">Rs.${t.save}</span></div>
   <div class="row tot"><span>Bundle price</span><span>Rs.${t.price}</span></div>
   <button class="btn" data-act="addBundle" ${full ? '' : 'disabled'}>Add bundle to bag</button>`;
    document.getElementById('mbar').innerHTML =
      `<div><b>Rs.${t.price}</b><span>${bState.picks.length} of ${n} shades chosen</span></div><button class="btn" data-act="addBundle" ${full ? '' : 'disabled'}>${full ? 'Add to bag' : `Pick ${left} more`}</button>`;
    document.querySelectorAll('[data-c]').forEach((el) => {
      const c = bState.picks.filter((h) => h === el.dataset.c).length;
      el.className = c ? 'c' : 'plus';
      el.textContent = c || '+';
      el.closest('.bp').classList.toggle('on', !!c);
    });
    document.querySelectorAll('.tier').forEach((b) => {
      const on = +b.dataset.tier === n;
      b.classList.toggle('sel', on);
      b.setAttribute('aria-pressed', on);
    });
  }


  /* ---------- 5. Virtual try-on ---------- */

  // Version of the face-tracking library used by the virtual try-on.
  const FM_VER = '0.4.1633559619';
  // Which face-tracking points outline each eye.
  const EYE_IDX = [
    [33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246],
    [263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386, 387, 388, 466],
  ];
  // Virtual try-on state: chosen shade, camera, lighting, settings.
  const VT = {
    autoStr: true,
    adaptive: true,
    light: null,
    ref: { s: [0.7644, 0.4882, 0.3815], w: [0.5554, 0.4262, 0.4023] },
    live: null,
    shade: 'golden-caramel-brown',
    fam: 'browns',
    img: null,
    eyes: null,
    amt: 0.9,
    size: 1,
    mode: 'model',
    showOrig: false,
    picks: [],
    fm: null,
    fmLoading: null,
    tex: {},
    busy: false,
  };
  // Loads a picture and waits until it is ready.
  const loadImg = (src) =>
    new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      noSet(i).src = src;
    });
  // Smooth 0-to-1 curve used for soft edges.
  const sstep = (a, b, x) => {
    const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
    return t * t * (3 - 2 * t);
  };
  // Prepares the lens pattern for a shade (from its lens photo).
  async function lensTex(h) {
    if (VT.tex[h]) return VT.tex[h];
    const im = await loadImg(BY[h].disc),
      W = im.naturalWidth,
      H = im.naturalHeight;
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const x = c.getContext('2d', { willReadFrequently: true });
    x.fillStyle = '#fff';
    x.fillRect(0, 0, W, H);
    x.drawImage(im, 0, 0);
    const d = x.getImageData(0, 0, W, H),
      p = d.data;
    let minX = W,
      minY = H,
      maxX = 0,
      maxY = 0;
    for (let y = 0; y < H; y++)
      for (let i = 0; i < W; i++) {
        const k = (y * W + i) * 4;
        const dd = Math.hypot(255 - p[k], 255 - p[k + 1], 255 - p[k + 2]) / 441;
        if (dd > 0.12) {
          if (i < minX) minX = i;
          if (i > maxX) maxX = i;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    const cx = (minX + maxX) / 2,
      cy = (minY + maxY) / 2,
      R = Math.max(10, (maxX - minX + (maxY - minY)) / 4);
    for (let y = 0; y < H; y++)
      for (let i = 0; i < W; i++) {
        const k = (y * W + i) * 4;
        const rr = Math.hypot(i - cx, y - cy) / R;
        const m = sstep(0.25, 0.37, rr) * (1 - sstep(0.93, 1.0, rr));
        const dd = Math.hypot(255 - p[k], 255 - p[k + 1], 255 - p[k + 2]) / 441;
        p[k + 3] = Math.round(255 * m * Math.min(0.97, 0.58 + dd * 1.7));
        const Lp = 0.299 * p[k] + 0.587 * p[k + 1] + 0.114 * p[k + 2];
        if (Lp > 185) {
          const f = (185 + (Lp - 185) * 0.3) / Lp;
          p[k] *= f;
          p[k + 1] *= f;
          p[k + 2] *= f;
        }
      }
    x.putImageData(d, 0, 0);
    const out = document.createElement('canvas');
    const S = Math.ceil(R * 2);
    out.width = out.height = S;
    out.getContext('2d').drawImage(c, cx - R, cy - R, S, S, 0, 0, S, S);
    return (VT.tex[h] = out);
  }
  // Shows a short status line in the try-on window.
  function vtStatus(t) {
    const el = document.getElementById('vtoS');
    if (!el) return;
    el.textContent = t || '';
    el.hidden = !t;
  }
  // Sets the picture the try-on draws on.
  function vtSetImg(img) {
    VT.img = img;
  }
  // Drawing surface for keeping the eye's natural highlights.
  const hiCv = document.createElement('canvas');
  // Drawing surfaces for reading light and building the lens.
  const lightCv = document.createElement('canvas'),
    lensCv = document.createElement('canvas');
  // Average colour of part of a picture.
  function sampleAvg(img, sx, sy, sw, sh, dw, dh) {
    const W = img.naturalWidth || img.width,
      H = img.naturalHeight || img.height;
    sx = Math.max(0, sx);
    sy = Math.max(0, sy);
    sw = Math.min(W - sx, sw);
    sh = Math.min(H - sy, sh);
    if (!(sw > 2 && sh > 2)) return null;
    lightCv.width = dw;
    lightCv.height = dh;
    const x = lightCv.getContext('2d', { willReadFrequently: true });
    x.drawImage(img, sx, sy, sw, sh, 0, 0, dw, dh);
    const d = x.getImageData(0, 0, dw, dh).data;
    let r = 0,
      g = 0,
      b = 0,
      n = 0;
    for (let k = 0; k < d.length; k += 4) {
      r += d[k];
      g += d[k + 1];
      b += d[k + 2];
      n++;
    }
    return [r / n, g / n, b / n];
  }
  // Real-light maths: screen value (0-255) to real light (0-1) and back.
  const LIN = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const v = i / 255;
    LIN[i] = v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  }
  const toScreen = (v) =>
    v <= 0 ? 0 : v >= 1 ? 255 : 255 * (v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055);
  // Reads the light on the face: the skin just under the eyes and the white of the eyes.
  // Using both keeps the reading steady for every skin tone and ignores the background.
  function estLight(img, eyes) {
    if (!eyes || !eyes.length) return null;
    const s = [0, 0, 0],
      w = [0, 0, 0];
    let n = 0,
      nw = 0;
    const lin = (a) => a.map((v) => LIN[Math.round(v)]);
    for (const e of eyes) {
      const a = sampleAvg(img, e.cx - e.r * 1.5, e.cy + e.r * 1.4, e.r * 3, e.r * 1.2, 24, 10);
      if (a) {
        lin(a).forEach((v, j) => (s[j] += v));
        n++;
      }
      // white of the eye: the brighter side of the iris (the other may be in shadow)
      let best = null,
        by = -1;
      for (const side of [-1, 1]) {
        const b = sampleAvg(img, e.cx + side * e.r * 1.6 - e.r * 0.25, e.cy - e.r * 0.15, e.r * 0.5, e.r * 0.3, 6, 4);
        if (!b) continue;
        const l = lin(b),
          y = 0.2126 * l[0] + 0.7152 * l[1] + 0.0722 * l[2];
        if (y > by) {
          by = y;
          best = l;
        }
      }
      if (best) {
        best.forEach((v, j) => (w[j] += v));
        nw++;
      }
    }
    if (!n) return null;
    return { s: s.map((v) => v / n), w: nw ? w.map((v) => v / nw) : null };
  }
  // Compares the light on the face with the studio photo: overall level (k, 1 = studio)
  // and the colour of the light (c), giving how much to light each colour of the lens (g).
  function lightAdjust(l) {
    if (!l) return { k: 1, c: [1, 1, 1], g: [1, 1, 1] };
    const R = VT.ref;
    const q = l.s.map((v, i) => {
      const a = v / R.s[i];
      return l.w ? Math.sqrt(a * (l.w[i] / R.w[i])) : a;
    });
    const Y = 0.2126 * q[0] + 0.7152 * q[1] + 0.0722 * q[2] || 0.001;
    const Yc = Math.max(0.03, Math.min(4, Y));
    const c = q.map((v) => Math.max(0.65, Math.min(1.4, 1 + 0.85 * (v / Y - 1))));
    return { k: Math.pow(Yc, 1 / 2.2), c, g: c.map((v) => v * Yc) };
  }
  // Applies the light and eyelid shadow to the lens pattern.
  function adjLens(tex, size, adj) {
    const S = Math.max(4, Math.ceil(size));
    lensCv.width = lensCv.height = S;
    const x = lensCv.getContext('2d');
    x.globalCompositeOperation = 'source-over';
    x.globalAlpha = 1;
    x.drawImage(tex, 0, 0, S, S);
    const m = adj.c.map((v) => v * adj.k),
      mx = Math.max(...m);
    // colour of the light + overall brightness
    x.globalCompositeOperation = 'multiply';
    x.fillStyle = `rgb(${m.map((v) => Math.round(255 * Math.min(1, v / Math.max(1, mx)))).join(',')})`;
    x.fillRect(0, 0, S, S);
    if (mx > 1) {
      x.globalCompositeOperation = 'screen';
      const s = Math.round(255 * Math.min(0.25, (mx - 1) * 0.9));
      x.fillStyle = `rgb(${s},${s},${s})`;
      x.fillRect(0, 0, S, S);
    }
    // shadow cast by the upper eyelid, stronger in dim or top-down light
    x.globalCompositeOperation = 'source-over';
    const g = x.createLinearGradient(0, 0, 0, S);
    const sh = 0.18 + 0.22 * (1 - Math.min(1, adj.k));
    g.addColorStop(0, `rgba(0,0,0,${sh})`);
    g.addColorStop(0.42, 'rgba(0,0,0,0)');
    x.fillStyle = g;
    x.fillRect(0, 0, S, S);
    x.globalCompositeOperation = 'destination-in';
    x.drawImage(tex, 0, 0, S, S);
    x.globalCompositeOperation = 'source-over';
    return lensCv;
  }
  // Drawing surface used to combine lens and eye.
  const compCv = document.createElement('canvas');
  // Builds the lens the way a printed coloured lens sits on a real iris:
  // lens hue on the eye's own light and shade, a partial lift in brightness, the iris texture
  // laid back over it, a dark outer (limbal) ring, and the pupil left untouched.
  function naturalLens(tex, e, r, adj, str, img = VT.img) {
    const S = Math.max(8, Math.ceil(r * 2)),
      T = adjLens(tex, S, adj),
      k = Math.min(1, adj.k);
    compCv.width = compCv.height = S;
    const c = compCv.getContext('2d');
    const src = [e.cx - r, e.cy - r, r * 2, r * 2];
    c.globalCompositeOperation = 'source-over';
    c.globalAlpha = 1;
    c.drawImage(img, ...src, 0, 0, S, S);
    c.globalCompositeOperation = 'color';
    c.globalAlpha = Math.min(1, str * 1.05);
    c.drawImage(T, 0, 0);
    c.globalCompositeOperation = 'source-over';
    c.globalAlpha = str * (0.42 + 0.2 * k);
    c.drawImage(T, 0, 0);
    c.globalCompositeOperation = 'soft-light';
    c.globalAlpha = 0.3 + 0.3 * (1 - k);
    c.drawImage(img, ...src, 0, 0, S, S);
    if (k < 0.85) {
      c.globalCompositeOperation = 'saturation';
      c.globalAlpha = (0.85 - k) * 0.55;
      c.fillStyle = '#808080';
      c.fillRect(0, 0, S, S);
    }
    c.globalCompositeOperation = 'source-over';
    c.globalAlpha = 1;
    const g = c.createRadialGradient(S / 2, S / 2, S * 0.36, S / 2, S / 2, S / 2);
    g.addColorStop(0, 'rgba(18,12,10,0)');
    g.addColorStop(0.72, 'rgba(18,12,10,0.05)');
    g.addColorStop(0.93, 'rgba(18,12,10,0.38)');
    g.addColorStop(1, 'rgba(18,12,10,0.2)');
    c.fillStyle = g;
    c.fillRect(0, 0, S, S);
    c.globalCompositeOperation = 'destination-in';
    c.drawImage(T, 0, 0);
    c.globalCompositeOperation = 'source-over';
    return compCv;
  }
  // Per-shade [brightness, colour richness], tuned so each lens matches its studio before/after photo.
  const LTUNE = {'soft-honey-hazel':[0.8,2.2],'golden-caramel-brown':[0.6,2.2],'cream-latte-brown':[0.6,1.6],'velvet-mocha-brown':[0.8,1],'rich-chestnut-brown':[0.6,1.2],'steel-ice-blue':[1,1.8],'aqua-sky-blue':[0.7,1.8],'smoky-ocean-blue':[0.7,2.2],'royal-sapphire-blue':[0.7,0.8],'ice-crystal-blue':[0.9,1.2],'deep-forest-green':[0.5,0.8],'soft-mint-green':[1,0.8],'olive-mist-green':[0.6,2.2],'sage-hazel-green':[0.9,1.6],'emerald-gold-green':[1,1.2],'soft-cloud-grey':[0.8,1.6],'steel-mist-grey':[0.8,0.8],'deep-charcoal-grey':[0.7,0.8],'champagne-ash-grey':[1.2,0.8],'pearl-silver-grey':[0.8,0.8]};
  // Drawing surface for the lens pattern in the live try-on.
  const lensPx = document.createElement('canvas');
  // Live try-on lens (camera and photos): the lens colour lit by the light measured on the face,
  // shaded by the eye's own iris texture, the eyelid shadow and a dark outer ring. The pupil stays clear.
  function liveLens(tex, e, r, adj, str, img) {
    const S = Math.max(8, Math.ceil(r * 2));
    lensPx.width = lensPx.height = S;
    const lx = lensPx.getContext('2d', { willReadFrequently: true });
    lx.drawImage(tex, 0, 0, S, S);
    const T = lx.getImageData(0, 0, S, S).data;
    compCv.width = compCv.height = S;
    const c = compCv.getContext('2d', { willReadFrequently: true });
    c.drawImage(img, e.cx - r, e.cy - r, r * 2, r * 2, 0, 0, S, S);
    const id = c.getImageData(0, 0, S, S),
      I = id.data;
    // average brightness of the iris under the lens
    let ys = 0,
      ws = 0;
    for (let k = 0; k < I.length; k += 4) {
      const w = T[k + 3];
      if (w) {
        ys += (0.2126 * LIN[I[k]] + 0.7152 * LIN[I[k + 1]] + 0.0722 * LIN[I[k + 2]]) * w;
        ws += w;
      }
    }
    const Ym = ws ? ys / ws : 0.05;
    const [K, SAT] = LTUNE[VT.shade] || [0.75, 1.4];
    // colours look duller in dim light (to the eye and to a phone camera), so the lens does too
    const dk = Math.min(1, adj.k / 0.75),
      sat = (1 + (SAT - 1) * dk) * (0.55 + 0.45 * dk);
    const g = adj.g,
      tg = 0.4 * Math.min(1, adj.k / 0.6),
      a0 = str * 1.22;
    for (let yy = 0; yy < S; yy++)
      for (let xx = 0; xx < S; xx++) {
        const k = (yy * S + xx) * 4;
        if (!T[k + 3]) continue;
        const a = Math.min(0.97, (T[k + 3] / 255) * a0);
        const i0 = LIN[I[k]],
          i1 = LIN[I[k + 1]],
          i2 = LIN[I[k + 2]];
        const y = 0.2126 * i0 + 0.7152 * i1 + 0.0722 * i2;
        const f = Math.max(0.55, Math.min(1.25, Math.pow((y + 0.002) / (Ym + 0.002), tg)));
        const rr = Math.hypot(xx - S / 2, yy - S / 2) / (S / 2);
        const sh = f * (1 - 0.35 * Math.max(0, 1 - yy / (S * 0.42))) * (1 - 0.55 * sstep(0.78, 0.97, rr)) * K;
        const t0 = LIN[T[k]],
          t1 = LIN[T[k + 1]],
          t2 = LIN[T[k + 2]],
          ty = 0.2126 * t0 + 0.7152 * t1 + 0.0722 * t2;
        I[k] = toScreen(i0 * (1 - a) + Math.max(0, ty + (t0 - ty) * sat) * g[0] * sh * a);
        I[k + 1] = toScreen(i1 * (1 - a) + Math.max(0, ty + (t1 - ty) * sat) * g[1] * sh * a);
        I[k + 2] = toScreen(i2 * (1 - a) + Math.max(0, ty + (t2 - ty) * sat) * g[2] * sh * a);
      }
    c.putImageData(id, 0, 0);
    c.globalCompositeOperation = 'destination-in';
    c.drawImage(lensPx, 0, 0);
    c.globalCompositeOperation = 'source-over';
    return compCv;
  }
  // How strong the lens colour should look, following the light on the face.
  // adj.k is the measured brightness (1 = normal room light): dimmer light -> softer colour
  // (down to 70%), brighter light -> stronger colour (up to 97%). Never drops so low it washes out.
  const autoStr = (adj) => {
    const k = adj && adj.k > 0 ? adj.k : 1;
    return Math.max(0.7, Math.min(0.97, 0.88 + 0.15 * Math.log2(k)));
  };
  // Draws the lens on both eyes.
  function renderEyes(x, img, eyes, tex, adj, str, size = 1) {
    const W = img.naturalWidth || img.width,
      H = img.naturalHeight || img.height;
    for (const e of eyes) {
      const r = e.r * 1.04 * size;
      if (!(r > 1)) continue;
      // keep the eye's own catchlights: copy the brightest pixels before the lens goes on
      const bx = Math.max(0, Math.floor(e.cx - r)),
        by = Math.max(0, Math.floor(e.cy - r)),
        bw = Math.min(W - bx, Math.ceil(r * 2)),
        bh = Math.min(H - by, Math.ceil(r * 2));
      let hi = null;
      // eye's own catchlights: the cut-off follows the light so they survive in dim rooms too
      const hl = adj.g ? Math.max(120, Math.min(200, 185 * Math.pow(adj.k, 0.6))) : 185;
      if (bw > 0 && bh > 0) {
        const d = x.getImageData(bx, by, bw, bh),
          q = d.data;
        for (let k = 0; k < q.length; k += 4) {
          const L = 0.299 * q[k] + 0.587 * q[k + 1] + 0.114 * q[k + 2];
          q[k + 3] = Math.round(255 * sstep(hl, hl + 50, L));
        }
        hiCv.width = bw;
        hiCv.height = bh;
        hiCv.getContext('2d').putImageData(d, 0, 0);
        hi = hiCv;
      }
      const comp = adj.g ? liveLens(tex, e, r, adj, str, img) : naturalLens(tex, e, r, adj, str, img);
      x.save();
      x.beginPath();
      if (e.poly) {
        e.poly.forEach(([px, py], i) => (i ? x.lineTo(px, py) : x.moveTo(px, py)));
        x.closePath();
      } else x.ellipse(e.cx, e.cy + r * 0.08, r * 1.25, r * 0.86, 0, 0, Math.PI * 2);
      x.clip();
      x.globalAlpha = 1;
      x.drawImage(comp, e.cx - r, e.cy - r, r * 2, r * 2);
      if (hi) {
        x.globalAlpha = 0.9;
        x.drawImage(hi, bx, by);
      }
      x.restore();
    }
  }
  // Draws one try-on frame.
  async function vtDraw() {
    const cv = document.getElementById('vtoC');
    if (!cv || !VT.img) return;
    const W = VT.img.naturalWidth || VT.img.width,
      H = VT.img.naturalHeight || VT.img.height;
    if (cv.width !== W || cv.height !== H) {
      cv.width = W;
      cv.height = H;
    }
    const x = cv.getContext('2d', { willReadFrequently: true });
    x.globalCompositeOperation = 'source-over';
    x.globalAlpha = 1;
    x.drawImage(VT.img, 0, 0, W, H);
    const eyes =
      VT.eyes || (VT.picks.length ? VT.picks.map((pt) => ({ cx: pt[0], cy: pt[1], r: pt[2] })) : null);
    if (!eyes || VT.showOrig) return;
    const tex = VT.tex[VT.shade] || (await lensTex(VT.shade));
    // read the light before the lens goes on; smooth it in live mode so it doesn't flicker
    const raw = VT.adaptive ? estLight(VT.img, eyes) : null;
    const now = performance.now();
    if (raw && VT.mode === 'live' && VT.light) {
      // settles in about half a second whatever the frame rate of the camera
      const m = Math.max(0.08, 1 - Math.exp(-(now - (VT.lightT || now)) / 350));
      const mix = (a, b) => (a && b ? a.map((v, i) => v + (b[i] - v) * m) : b);
      VT.light = { s: mix(VT.light.s, raw.s), w: mix(VT.light.w, raw.w) };
    } else VT.light = raw;
    VT.lightT = now;
    const adj = lightAdjust(VT.light);
    VT.adj = adj;
    vtLightUI(adj);
    const str = VT.autoStr ? autoStr(VT.adaptive ? adj : { k: 1 }) : VT.amt;
    if (VT.autoStr) {
      const v = Math.round(str * 100),
        sl = document.getElementById('vtoAmt');
      if (sl && +sl.value !== v) {
        sl.value = v;
        document.getElementById('vtoAmtV').textContent = v + '%';
      }
    }
    renderEyes(x, VT.img, eyes, tex, adj, str, VT.size);
  }
  // Turns face-tracking points into eye centres and sizes.
  function lmToEyes(lm, W, H) {
    const P = (i) => [lm[i].x * W, lm[i].y * H];
    const iris = [468, 473].map((ci) => {
      const [cx, cy] = P(ci);
      const r = [1, 2, 3, 4].reduce((s, k) => s + Math.hypot(P(ci + k)[0] - cx, P(ci + k)[1] - cy), 0) / 4;
      return { cx, cy, r };
    });
    const polys = EYE_IDX.map((a) => a.map(P));
    const cen = (A) => A.reduce((s, q) => [s[0] + q[0] / A.length, s[1] + q[1] / A.length], [0, 0]);
    return iris.map((e) => ({
      ...e,
      poly: polys.slice().sort((A, B) => {
        const a = cen(A),
          b = cen(B);
        return Math.hypot(a[0] - e.cx, a[1] - e.cy) - Math.hypot(b[0] - e.cx, b[1] - e.cy);
      })[0],
    }));
  }
  // Message when the browser blocks the camera.
  const CAM_BLOCKED =
    "Camera access is blocked. Allow camera access in your browser's address bar, then press Turn on camera.";
  // Message when no camera is found.
  const CAM_NONE = "We couldn't find a camera on this device. Connect a camera, then press Turn on camera.";
  // Message when the camera is off.
  const CAM_OFF = 'Your camera is off. Turn it on to see any april. shade on your own eyes.';
  // Shows the "camera off / blocked / busy" screen with a button.
  function vtIdle(title, msg, busy) {
    VT.img = null;
    VT.eyes = null;
    VT.picks = [];
    VT.mode = 'idle';
    const st = document.getElementById('vtoStage');
    if (!st) return;
    st.classList.add('idle');
    st.classList.remove('pick');
    document.getElementById('vtoIdleT').textContent = title;
    document.getElementById('vtoIdleM').textContent = msg || '';
    document.getElementById('vtoIdleB').hidden = !!busy;
    document.getElementById('vtoIdleSpin').hidden = !busy;
    const lt = document.getElementById('vtoLight');
    if (lt) lt.hidden = true;
    vtStatus('');
    vtButtons();
  }
  // Switches the try-on window to the live camera.
  function vtShowCam() {
    const st = document.getElementById('vtoStage');
    if (st) st.classList.remove('idle');
  }
  // Updates the try-on buttons (live / photo / hold).
  function vtButtons() {
    const lb = document.getElementById('vtoLive'),
      cb = document.getElementById('vtoSnap');
    if (!lb) return;
    lb.innerHTML = VT.live ? 'Stop camera' : '<span class="dotlive"></span>Turn on camera';
    cb.hidden = !VT.live;
    const hb = document.getElementById('vtoHold');
    if (hb) hb.hidden = !VT.img;
  }
  // Turns the camera off.
  function vtStop() {
    if (!VT.live) return;
    cancelAnimationFrame(VT.live.raf);
    VT.live.stream.getTracks().forEach((t) => t.stop());
    VT.live = null;
    vtButtons();
  }
  // Turns the camera on and tracks the face so the lens follows the eyes.
  async function vtLive() {
    if (VT.live) {
      vtStop();
      vtIdle('Camera off', CAM_OFF);
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      vtIdle('Camera unavailable', CAM_BLOCKED);
      return;
    }
    vtIdle('Starting your camera…', 'Allow camera access when your browser asks.', true);
    vtStatus('Starting your camera…');
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', ...(innerHeight > innerWidth ? { width: { ideal: 960 }, height: { ideal: 1280 }, aspectRatio: { ideal: 0.75 } } : { width: { ideal: 1280 }, height: { ideal: 720 } }) },
        audio: false,
      });
    } catch (e) {
      if (e && e.name === 'NotFoundError') vtIdle('No camera found', CAM_NONE);
      else if (e && /NotReadable|TrackStart|Abort/.test(e.name))
        vtIdle(
          'Camera is busy',
          'Another app or browser tab is using your camera. Close it (Zoom, Teams, Camera app or another try-on tab), then press Turn on camera.',
        );
      else vtIdle('Camera access needed', CAM_BLOCKED);
      return;
    }
    vtShowCam();
    const video = document.createElement('video');
    video.playsInline = true;
    video.muted = true;
    video.srcObject = stream;
    try {
      await video.play();
    } catch (e) {}
    const frame = document.createElement('canvas');
    frame.width = video.videoWidth || 1280;
    frame.height = video.videoHeight || 720;
    VT.live = { stream, video, frame, raf: 0 };
    VT.mode = 'live';
    VT.eyes = null;
    VT.picks = [];
    VT.img = frame;
    document.getElementById('vtoStage').classList.remove('pick');
    vtPanel();
    vtButtons();
    vtStatus('Loading eye tracking…');
    let fm = null;
    try {
      fm = await fmLoad();
    } catch (e) {
      vtStatus(
        "Eye tracking didn't load, so lenses can't follow your eyes yet. Check your connection and try again.",
      );
    }
    if (!VT.live || VT.live.video !== video) return;
    let prev = null;
    if (fm)
      fm.onResults((res) => {
        if (!VT.live || VT.live.video !== video) return;
        const lm = res.multiFaceLandmarks && res.multiFaceLandmarks[0];
        if (lm) {
          const e = lmToEyes(lm, frame.width, frame.height);
          VT.eyes = prev
            ? e.map((n, i) => ({
                ...n,
                cx: prev[i].cx * 0.4 + n.cx * 0.6,
                cy: prev[i].cy * 0.4 + n.cy * 0.6,
                r: prev[i].r * 0.6 + n.r * 0.4,
              }))
            : e;
          prev = VT.eyes;
          vtStatus('');
        } else {
          VT.eyes = null;
          prev = null;
          vtStatus('Look at the camera so we can find your eyes.');
        }
      });
    const fx = frame.getContext('2d');
    const loop = async () => {
      if (!VT.live || VT.live.video !== video) return;
      if (video.videoWidth && frame.width !== video.videoWidth) {
        frame.width = video.videoWidth;
        frame.height = video.videoHeight;
      }
      fx.save();
      fx.translate(frame.width, 0);
      fx.scale(-1, 1);
      fx.drawImage(video, 0, 0, frame.width, frame.height);
      fx.restore();
      if (fm) {
        try {
          await fm.send({ image: frame });
        } catch (e) {}
      }
      await vtDraw();
      if (VT.live && VT.live.video === video) VT.live.raf = requestAnimationFrame(loop);
    };
    loop();
  }
  // Saves a photo of the current try-on.
  function vtSnap() {
    if (!VT.live) return;
    const f = VT.live.frame,
      c = document.createElement('canvas');
    c.width = f.width;
    c.height = f.height;
    c.getContext('2d').drawImage(f, 0, 0);
    const eyes = VT.eyes;
    vtStop();
    vtSetImg(c);
    VT.mode = eyes ? 'photo' : 'manual';
    VT.eyes = eyes;
    vtPanel();
    vtDraw();
    vtStatus(
      eyes
        ? 'Photo taken. Switch shades to compare them on your eyes.'
        : 'Tap the centre of each eye to place the lenses.',
    );
    if (!eyes) document.getElementById('vtoStage').classList.add('pick');
  }
  // Shows the lighting tip in the try-on window.
  function vtLightUI(adj) {
    const el = document.getElementById('vtoLight');
    if (!el) return;
    if (!VT.adaptive || VT.mode === 'model' || !VT.light) {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    const k = adj.k,
      c = adj.c,
      warm = c[0] - c[2];
    const lvl = k < 0.5 ? 'Low light' : k > 1.05 ? 'Bright light' : 'Good light';
    const tone = warm > 0.15 ? ', warm' : warm < -0.15 ? ', cool' : '';
    el.innerHTML = `<i class="${k < 0.5 ? 'lo' : 'ok'}"></i>${lvl}${tone} · lens adjusted`;
    el.title = k < 0.5 ? 'Face a window or lamp for a truer preview.' : '';
    const tip = document.getElementById('vtoS');
    if (VT.mode === 'live' && k < 0.45 && tip && tip.hidden) {
      tip.textContent = "It's quite dark. Face a window or lamp for a truer colour.";
      tip.hidden = false;
      VT.lowTip = true;
    } else if (VT.lowTip && k > 0.55 && tip) {
      tip.hidden = true;
      VT.lowTip = false;
    }
  }
  // Try-on side panel: shade list and controls.
  function vtPanel() {
    const p = BY[VT.shade];
    const cur = document.getElementById('vtoCur');
    if (!cur) return;
    cur.innerHTML = `<img src="${p.disc}" alt="${esc(p.n)} lens"><div><b>${esc(p.n)}</b><span>${moneyC(p.price)} ${p.was > p.price ? `<s>${moneyC(p.was)}</s>` : ''}</span><br><a href="${U.p(p.h)}">View product</a></div>`;
    document
      .querySelectorAll('.vto-fams button')
      .forEach((b) => b.classList.toggle('on', b.dataset.vfam === VT.fam));
    document.getElementById('vtoSw').innerHTML = LENS.filter((l) => l.f === VT.fam)
      .map(
        (l) =>
          `<button class="${l.h === VT.shade ? 'on' : ''}" data-vshade="${l.h}" aria-pressed="${l.h === VT.shade}"><img src="${l.disc}" alt="${esc(l.n)} lens">${esc(l.n)}</button>`,
      )
      .join('');
    document.getElementById('vtoAdd').dataset.addq = p.h;
    document.getElementById('vtoSizeRow').hidden = !(VT.mode === 'manual');
  }
  // Loads the face-tracking library (only when the try-on is used).
  function fmLoad() {
    if (VT.fmLoading) return VT.fmLoading;
    VT.fmLoading = new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@${FM_VER}/face_mesh.js`;
      s.crossOrigin = 'anonymous';
      s.onload = async () => {
        try {
          const fm = new window.FaceMesh({
            locateFile: (f) =>
              /\.js$/.test(f)
                ? `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@${FM_VER}/${f}`
                : `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@${FM_VER}/${f}`,
          });
          fm.setOptions({
            maxNumFaces: 1,
            refineLandmarks: true,
            minDetectionConfidence: 0.5,
            minTrackingConfidence: 0.5,
          });
          await fm.initialize();
          res(fm);
        } catch (e) {
          rej(e);
        }
      };
      s.onerror = () => rej(new Error('script'));
      document.head.appendChild(s);
      setTimeout(() => rej(new Error('timeout')), 45000);
    });
    VT.fmLoading.catch(() => {
      VT.fmLoading = null;
    });
    return VT.fmLoading;
  }
  // Lets the customer tap an eye to place the lens by hand.
  function vtCanvasClick(e) {
    if (VT.mode !== 'manual') return;
    const cv = e.currentTarget,
      rc = cv.getBoundingClientRect();
    const x = ((e.clientX - rc.left) / rc.width) * cv.width,
      y = ((e.clientY - rc.top) / rc.height) * cv.height;
    if (VT.picks.length >= 2) VT.picks = [];
    VT.picks.push([x, y, cv.width * 0.026]);
    vtStatus(
      VT.picks.length === 1
        ? 'Now tap the centre of the other eye.'
        : 'Use Lens size to fit the lens to your iris. Tap again to re-place.',
    );
    if (VT.picks.length === 2) document.getElementById('vtoStage').classList.remove('pick');
    vtDraw();
  }
  // Virtual Try-On page.
  const pTry =
    () => `<section class="blk"><div class="wrap"><div class="crumbs"><a href="/">Home</a> / Virtual Try-On</div>
 <h1 class="bh">Virtual Try-On</h1><p class="bsub">Try it on. Before you put it on. Your camera turns on so you can see any shade on your own eyes, live.</p>
 <div class="vto">
  <div><div class="vto-stage idle" id="vtoStage"><div class="vto-idle"><svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8h3l2-3h6l2 3h3v11H4z"/><circle cx="12" cy="13" r="3.5"/></svg><b id="vtoIdleT">Starting your camera…</b><span id="vtoIdleM">Allow camera access when your browser asks.</span><i class="vspin" id="vtoIdleSpin"></i><button class="btn" id="vtoIdleB" hidden>Turn on camera</button></div><canvas id="vtoC" width="928" height="615" aria-label="Photo with the selected april. lens shade applied"></canvas><div class="vto-status"><span id="vtoS" hidden></span></div></div>
   <div class="vto-tools">
    <button class="btn" id="vtoLive"><span class="dotlive"></span>Live camera</button>
    <button class="btn line" id="vtoSnap" hidden>Take photo</button>
    <button class="btn line vto-hold" id="vtoHold">Hold to see before</button>
   </div></div>
  <aside class="vto-panel">
   <div class="vto-cur" id="vtoCur"></div>
   <div class="vto-fams" role="group" aria-label="Shade family">${['browns', 'blues', 'greens', 'greys'].map((f) => `<button data-vfam="${f}">${FAM[f]}</button>`).join('')}</div>
   <div class="vto-sw" id="vtoSw" role="group" aria-label="Shades"></div>
   <div class="vto-row"><div class="vlab"><label for="vtoAmt">Colour strength</label><label class="vauto"><input type="checkbox" id="vtoAuto" checked>Auto</label><span id="vtoAmtV">90%</span></div><input type="range" id="vtoAmt" min="40" max="100" value="90"></div>
   <div class="vto-row vto-adapt"><label for="vtoAdapt"><span>Match my lighting</span><input type="checkbox" id="vtoAdapt" checked></label><div class="vto-lt" id="vtoLight" hidden></div></div>
   <div class="vto-row" id="vtoSizeRow" hidden><label for="vtoSize"><span>Lens size</span></label><input type="range" id="vtoSize" min="50" max="200" value="100"></div>
   <button class="btn full" id="vtoAdd" data-addq="golden-caramel-brown">${BAG}Add to Bag</button>
   <p class="vto-note">Your camera stays on your device. Nothing is uploaded or saved. Colour on screen is a preview and can look different with your natural eye colour and lighting.</p>
  </aside></div>
 <div class="vto-tips"><div><b>Face the camera</b>Look straight ahead with both eyes open.</div><div><b>Use soft daylight</b>Avoid strong shadows and glasses.</div><div><b>Get close</b>Your eyes should be clearly visible in the photo.</div></div>
</div></section>`;
  // Starts the Virtual Try-On page.
  function vtInit() {
    if (!BY[VT.shade]) VT.shade = 'golden-caramel-brown';
    VT.fam = BY[VT.shade].f;
    document.getElementById('vtoAmt').value = Math.round(VT.amt * 100);
    document.getElementById('vtoAmtV').textContent = Math.round(VT.amt * 100) + '%';
    vtPanel();
    // open straight into the live camera; the model photo shows underneath until the camera is ready (or if it's blocked)
    document.getElementById('vtoIdleB').onclick = vtLive;
    vtIdle('Starting your camera…', 'Allow camera access when your browser asks.', true);
    vtLive();
    const cv = document.getElementById('vtoC');
    cv.addEventListener('click', vtCanvasClick);
    document.getElementById('vtoLive').onclick = vtLive;
    document.getElementById('vtoSnap').onclick = vtSnap;
    vtButtons();
    const hold = document.getElementById('vtoHold'),
      on = () => {
        VT.showOrig = true;
        vtDraw();
      },
      off = () => {
        if (VT.showOrig) {
          VT.showOrig = false;
          vtDraw();
        }
      };
    hold.addEventListener('pointerdown', on);
    ['pointerup', 'pointerleave', 'pointercancel'].forEach((t) => hold.addEventListener(t, off));
    hold.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        on();
      }
    });
    hold.addEventListener('keyup', off);
    document.getElementById('vtoAmt').oninput = (e) => {
      VT.amt = e.target.value / 100;
      VT.autoStr = false;
      document.getElementById('vtoAuto').checked = false;
      document.getElementById('vtoAmtV').textContent = e.target.value + '%';
      vtDraw();
    };
    document.getElementById('vtoAuto').checked = VT.autoStr;
    document.getElementById('vtoAuto').onchange = (e) => {
      VT.autoStr = e.target.checked;
      if (!VT.autoStr) VT.amt = document.getElementById('vtoAmt').value / 100;
      vtDraw();
    };
    document.getElementById('vtoAdapt').checked = VT.adaptive;
    document.getElementById('vtoAdapt').onchange = (e) => {
      VT.adaptive = e.target.checked;
      VT.light = null;
      vtDraw();
    };
    document.getElementById('vtoSize').oninput = (e) => {
      VT.size = e.target.value / 100;
      vtDraw();
    };
    document.querySelector('.vto-panel').addEventListener('click', (e) => {
      const b = e.target.closest('button');
      if (!b) return;
      if (b.dataset.vfam) {
        VT.fam = b.dataset.vfam;
        vtPanel();
      } else if (b.dataset.vshade) {
        VT.shade = b.dataset.vshade;
        vtPanel();
        vtDraw();
      }
    });
  }


  /* ---------- 6. Clicks, scrolling rows and sliders ---------- */

  // Makes a product row scroll with arrows, dots, drag and hover.
  function bindRail(el) {
    const dots = document.querySelector(`.dots[data-rail="${el.id}"]`);
    if (!dots) return;
    const bs = [...dots.children];
    const upd = () => {
      const max = el.scrollWidth - el.clientWidth;
      const i = max <= 0 ? 0 : Math.round((el.scrollLeft / max) * (bs.length - 1));
      bs.forEach((b, j) => b.classList.toggle('on', j === i));
      dots.style.visibility = max <= 0 ? 'hidden' : 'visible';
    };
    const w = el.parentElement,
      arrows = [...w.querySelectorAll('.ra')];
    const updA = () => {
      const max = el.scrollWidth - el.clientWidth;
      arrows[0].disabled = el.scrollLeft <= 2;
      arrows[1].disabled = el.scrollLeft >= max - 2;
    };
    el.addEventListener(
      'scroll',
      () => {
        upd();
        updA();
      },
      { passive: true },
    );
    upd();
    updA();
    const step = () => {
      const c = el.querySelector('.pc');
      return c ? c.getBoundingClientRect().width + 18 : 260;
    };
    arrows.forEach(
      (a) => (a.onclick = () => el.scrollBy({ left: +a.dataset.dir * step() * 2, behavior: 'smooth' })),
    );
    if (matchMedia('(hover:hover)').matches && !matchMedia('(prefers-reduced-motion:reduce)').matches) {
      let v = 0,
        raf = 0;
      const loop = () => {
        if (!v) {
          raf = 0;
          el.style.scrollSnapType = '';
          return;
        }
        el.scrollLeft += v;
        raf = requestAnimationFrame(loop);
      };
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect(),
          x = e.clientX - r.left,
          z = Math.min(200, r.width * 0.2);
        v =
          x > r.width - z
            ? 2 + ((x - (r.width - z)) / z) * 10
            : x < z
              ? -(2 + ((z - x) / z) * 10)
              : el.scrollLeft < el.scrollWidth - el.clientWidth - 2
                ? 1.2
                : 0;
        if (v) {
          el.style.scrollSnapType = 'none';
          if (!raf) raf = requestAnimationFrame(loop);
        }
      });
      el.addEventListener('mouseleave', () => {
        v = 0;
      });
    }
    bs.forEach(
      (b, j) =>
        (b.onclick = () => {
          const max = el.scrollWidth - el.clientWidth;
          el.scrollTo({ left: (max * j) / (bs.length - 1), behavior: 'smooth' });
        }),
    );
  }
  window.addEventListener('resize', () =>
    document.querySelectorAll('.apr .rail').forEach((el) => el.dispatchEvent(new Event('scroll'))),
  );
  // Moves the before/after slider.
  const baSet = (el, x) => {
    const r = el.getBoundingClientRect();
    const v = Math.max(0, Math.min(100, ((x - r.left) / r.width) * 100));
    el.style.setProperty('--p', v + '%');
    const i = el.querySelector('input');
    if (i) i.value = Math.round(v);
  };
  // Product page panel where the live try-on opens.
  let pvBox = null,
    pvHtml = '';
  // Opens the live camera try-on inside the product page, in that shade.
  function pdpTry(h, box) {
    if (pvBox) return;
    pvBox = box;
    pvHtml = box.innerHTML;
    const p = BY[h];
    VT.shade = h;
    VT.fam = p.f;
    VT.eyes = null;
    VT.picks = [];
    box.classList.add('pv-on');
    box.innerHTML = `<div class="pv"><div class="pv-head"><img src="${p.disc}" alt="${esc(p.n)} lens"><div><span class="ey">Live try-on</span><b>${esc(p.n)}</b></div><button class="pv-x" id="pvClose" aria-label="Close camera">×</button></div>
  <div class="vto-stage idle" id="vtoStage"><div class="vto-idle"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8h3l2-3h6l2 3h3v11H4z"/><circle cx="12" cy="13" r="3.5"/></svg><b id="vtoIdleT">Starting your camera…</b><span id="vtoIdleM">Allow camera access when your browser asks.</span><i class="vspin" id="vtoIdleSpin"></i><button class="btn" id="vtoIdleB" hidden>Turn on camera</button></div><canvas id="vtoC" width="928" height="615" aria-label="Your camera with ${esc(p.n)} lenses"></canvas><div class="vto-status"><span id="vtoS" hidden></span></div></div>
  <div class="vto-tools"><button class="btn line" id="vtoLive">Stop camera</button><button class="btn line" id="vtoSnap" hidden>Take photo</button><button class="btn line vto-hold" id="vtoHold">Hold to see before</button></div>
  <div class="pv-foot"><button class="btn addbag" data-addq="${p.h}">${BAG}Add to Bag</button><a href="${U.tryon(h)}">Try other shades</a></div><span id="vtoLight" hidden></span></div>`;
    document.getElementById('vtoIdleB').onclick = vtLive;
    document.getElementById('vtoLive').onclick = vtLive;
    document.getElementById('vtoSnap').onclick = vtSnap;
    document.getElementById('vtoC').addEventListener('click', vtCanvasClick);
    const hold = document.getElementById('vtoHold'),
      on = () => {
        VT.showOrig = true;
        vtDraw();
      },
      off = () => {
        if (VT.showOrig) {
          VT.showOrig = false;
          vtDraw();
        }
      };
    hold.addEventListener('pointerdown', on);
    ['pointerup', 'pointerleave', 'pointercancel'].forEach((t) => hold.addEventListener(t, off));
    vtLive();
  }
  // Closes the product page try-on and turns the camera off.
  function pdpTryClose() {
    if (!pvBox) return;
    vtStop();
    VT.img = null;
    pvBox.classList.remove('pv-on');
    pvBox.innerHTML = pvHtml;
    pvBox = null;
  }
  window.addEventListener('pagehide', () => {
    try {
      vtStop();
    } catch (e) {}
  });
  // All clicks and taps on the page: add to bag, quantity, tabs, bundle picks, try-on, photos.
  function bindEvents(app) {
    app.addEventListener('click', (e) => {
      const t = e.target.closest('button,a');
      if (!t || !app.contains(t)) return;
      if (t.dataset.tryshade && t.closest('.see-t') && BY[t.dataset.tryshade]) {
        e.preventDefault();
        pdpTry(t.dataset.tryshade, t.closest('.see-t'));
        return;
      }
      if (t.id === 'mpMore') {
        app.querySelector('#mpDesc').classList.add('open');
        t.remove();
        return;
      }
      if (t.id === 'pvClose') {
        pdpTryClose();
        return;
      }
      if (t.dataset.tryshade) {
        e.preventDefault();
        location.href = U.tryon(t.dataset.tryshade);
        return;
      }
      if (t.dataset.bundle) {
        location.href = U.bundle(+t.dataset.bundle);
        return;
      }
      if (t.dataset.fam && t.closest('.stabs') && !t.closest('.apr[data-fam]')) {
        e.preventDefault();
        app.innerHTML = rsp(pFamily(t.dataset.fam));
        history.replaceState(null, '', U.fam(t.dataset.fam));
        return;
      }
      if (t.dataset.tier) {
        bState.n = +t.dataset.tier;
        bState.picks = bState.picks.slice(0, bState.n);
        renderSlots();
      } else if (t.dataset.bpick) {
        if (bState.picks.length < bState.n) bState.picks.push(t.dataset.bpick);
        else {
          bState.picks[bState.n - 1] = t.dataset.bpick;
          toast('Swapped your last shade');
        }
        renderSlots();
      } else if (t.dataset.unpick !== undefined) {
        bState.picks.splice(+t.dataset.unpick, 1);
        renderSlots();
      } else if (t.dataset.act === 'addBundle') {
        if (bState.picks.length === bState.n) cartAdd(aggItems(bState.picks));
      } else if (t.dataset.add) {
        cartAdd([{ id: BY[t.dataset.add] && BY[t.dataset.add].v, quantity: 1 }]);
      } else if (t.dataset.mpsrc) {
        const bx = app.querySelector('#mpBox');
        noSet(app.querySelector('#mpMain')).src = t.dataset.mpsrc;
        bx.classList.toggle('is-disc', t.dataset.fit === 'disc');
        t.parentElement.querySelectorAll('button').forEach((b) => b.classList.toggle('on', b === t));
      } else if (t.dataset.thumb) {
        noSet(app.querySelector('#mainImg')).src = t.dataset.thumb;
        t.parentElement.querySelectorAll('button').forEach((b) => b.classList.toggle('on', b === t));
      } else if (t.id === 'qp') {
        qty = Math.min(10, qty + 1);
        app.querySelector('#qo').textContent = qty;
        mpSync(app);
      } else if (t.id === 'qm') {
        qty = Math.max(1, qty - 1);
        app.querySelector('#qo').textContent = qty;
        mpSync(app);
      } else if (t.dataset.addq) {
        cartAdd([{ id: BY[t.dataset.addq] && BY[t.dataset.addq].v, quantity: qty }]);
      } else if (t.dataset.addsol !== undefined) {
        cartAdd([{ id: SOL.v, quantity: qty }]);
      }
    });
    app.addEventListener('input', (e) => {
      if (e.target.id === 'baR') app.querySelector('#ba').style.setProperty('--p', e.target.value + '%');
    });
    app.addEventListener('pointerdown', (e) => {
      const el = e.target.closest('.ba');
      if (!el) return;
      baSet(el, e.clientX);
      el.setPointerCapture(e.pointerId);
      el.dataset.drag = '1';
    });
    app.addEventListener('pointermove', (e) => {
      const el = e.target.closest('.ba');
      if (el && el.dataset.drag) baSet(el, e.clientX);
    });
    app.addEventListener('pointerup', (e) => {
      const el = e.target.closest('.ba');
      if (el) delete el.dataset.drag;
    });
    app.addEventListener('pointercancel', (e) => {
      const el = e.target.closest('.ba');
      if (el) delete el.dataset.drag;
    });
    app.addEventListener('submit', (e) => {
      if (e.target.id === 'subForm') {
        const v = app.querySelector('#subEmail').value;
        if (!/^\S+@\S+\.\S+$/.test(v)) {
          e.preventDefault();
          app.querySelector('#subOk').textContent = 'Enter an email address like name@example.com.';
        }
      }
    });
  }

  /* ---------- 7. Start-up ---------- */

  // Draws the right page (home, product, shade, bundle or try-on) into the april. section.
  function mount(root) {
    if (root.dataset.mounted) return;
    root.dataset.mounted = '1';
    const app = root.querySelector('.apr-app');
    if (!app) return;
    if (!document.getElementById('aprToast'))
      root.insertAdjacentHTML(
        'beforeend',
        '<div class="toast" id="aprToast" role="status" aria-live="polite"></div>',
      );
    const q = new URLSearchParams(location.search),
      view = root.dataset.view;
    if (view === 'home') {
      app.innerHTML = rsp(pHome());
      hStart();
      vidBind(app);
    } else if (view === 'shades') {
      let f = root.dataset.fam || q.get('f');
      if (!FAM[f]) f = 'browns';
      app.innerHTML = rsp(pFamily(f));
    } else if (view === 'bundle') {
      const n = +q.get('n');
      if ([2, 3, 4].includes(n)) bState.n = n;
      app.innerHTML = rsp(pBundle());
      renderSlots();
      document.body.classList.add('has-mbar');
    } else if (view === 'tryon') {
      const s = q.get('shade');
      if (BY[s]) VT.shade = s;
      app.innerHTML = rsp(pTry());
      vtInit();
    } else if (view === 'product') {
      const h = root.dataset.product;
      app.innerHTML = rsp(BY[h] ? pProduct(BY[h]) : pSolution());
      if (BY[h]) {
        root.classList.add('mp');
        mpBind(app);
        mpSpec(app);
        mpReviews(root, app);
      }
    }
    homeReviews(root, app);
    featuredBest(app);
    app.querySelectorAll('.rail').forEach(bindRail);
    bindEvents(app);
  }
  // Header menu: mobile menu, shade drop-down, current page highlight, bag count, Bundles filter on the Shop page.
  function header(h) {
    if (!h || h.dataset.mounted) return;
    h.dataset.mounted = '1';
    const nav = h.querySelector('#nav'),
      mb = h.querySelector('#menuBtn'),
      dd = h.querySelector('#dd'),
      ddt = h.querySelector('#ddt');
    if (mb)
      mb.onclick = () => {
        const o = nav.classList.toggle('open');
        mb.setAttribute('aria-expanded', o);
      };
    if (ddt)
      ddt.onclick = (e) => {
        e.preventDefault();
        const o = dd.classList.toggle('open');
        ddt.setAttribute('aria-expanded', o);
      };
    const q = new URLSearchParams(location.search),
      v = q.get('view') || '',
      p = location.pathname;
    const key =
      v === 'april-shades' || /^\/collections\/(browns|blues|greens|greys)\b/.test(p)
        ? 'shades'
        : v === 'april-bundle' || p.indexOf('/pages/bundle') === 0
          ? 'bundle'
          : v === 'april-tryon' || p.indexOf('/pages/virtual') === 0
            ? 'tryon'
            : p === '/'
              ? 'home'
              : p.indexOf('/collections') === 0 || p.indexOf('/products') === 0
                ? 'shop'
                : '';
    h.querySelectorAll('[data-nav]').forEach((a) => a.classList.toggle('cur', a.dataset.nav === key));
    if (!window.__aprCart) {
      window.__aprCart = 1;
      const of = window.fetch;
      window.fetch = function (...a) {
        const r = of.apply(this, a);
        try {
          const u = String((a[0] && a[0].url) || a[0]);
          if (/\/cart\/(add|change|update|clear)/.test(u))
            r.then(
              () => setTimeout(setCount, 250),
              () => {},
            );
        } catch (e) {}
        return r;
      };
      ['cart:update', 'cart:updated', 'cart:change'].forEach((ev) =>
        document.addEventListener(ev, () => setTimeout(setCount, 250)),
      );
      setCount();
    }
    if (p.indexOf('/collections/') === 0 && !v) {
      const html = `<div class="apr-bf"><div class="apr-bf-h">Bundles</div>${TIERS.map((t) => `<a href="${U.bundle(t.n)}"><span>Pick your ${t.n}</span><b>Rs.${t.price}</b><i>Save Rs.${t.save}</i></a>`).join('')}</div>`;
      const inj = () =>
        document.querySelectorAll('.facets__filters-wrapper').forEach((w) => {
          if (!w.querySelector('.apr-bf')) w.insertAdjacentHTML('beforeend', html);
        });
      inj();
      let tm = 0;
      new MutationObserver(() => {
        clearTimeout(tm);
        tm = setTimeout(inj, 120);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }
  // Bundle & Save: colour filter buttons above the shade grid.
  document.addEventListener('click', (e) => {
    const b = e.target.closest('.bchips [data-bfam]');
    if (!b) return;
    b.parentElement.querySelectorAll('button').forEach((x) => {
      x.classList.toggle('on', x === b);
      x.setAttribute('aria-pressed', String(x === b));
    });
    const v = b.dataset.bfam;
    document.querySelectorAll('.pfam[data-fam]').forEach((p) => (p.hidden = !(v === 'all' || p.dataset.fam === v)));
  });
  // Search page: shows matching shades as the customer types (Shopify predictive search), matched letters in bold.
  function searchSuggest() {
    const inp = document.querySelector('input.search-page-input');
    if (!inp || inp.dataset.aprSug) return;
    inp.dataset.aprSug = '1';
    inp.setAttribute('autocomplete', 'off');
    const form = inp.closest('search-page-input-component') || inp.closest('form') || inp.parentElement;
    form.classList.add('apr-sug-host');
    const box = document.createElement('div');
    box.className = 'apr-sug';
    box.hidden = true;
    form.appendChild(box);
    const hl = (name, q) => {
      const i = name.toLowerCase().indexOf(q.toLowerCase());
      return i < 0 ? esc(name) : esc(name.slice(0, i)) + '<b>' + esc(name.slice(i, i + q.length)) + '</b>' + esc(name.slice(i + q.length));
    };
    const rs = (v) => 'Rs.' + Math.round(+v || 0).toLocaleString('en-US');
    const thumb = (u) => (u ? u + (u.indexOf('?') < 0 ? '?' : '&') + 'width=120' : '');
    let t = 0;
    let last = '';
    const run = async () => {
      const q = inp.value.trim();
      if (q === last) return;
      last = q;
      if (!q) {
        box.hidden = true;
        box.innerHTML = '';
        return;
      }
      try {
        const r = await fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6');
        const j = await r.json();
        if (inp.value.trim() !== q) return;
        const ps = (j.resources && j.resources.results && j.resources.results.products) || [];
        const all = '<a class="apr-sug-all" href="/search?q=' + encodeURIComponent(q) + '&options[prefix]=last">See all results for \u201c' + esc(q) + '\u201d</a>';
        box.innerHTML = ps.length
          ? ps.map((p) => '<a class="apr-sug-i" href="' + p.url + '"><img src="' + thumb(p.image) + '" alt="" width="48" height="48"><span class="apr-sug-n">' + hl(p.title, q) + '</span><span class="apr-sug-p">' + rs(p.price) + (+p.compare_at_price_max > +p.price ? ' <s>' + rs(p.compare_at_price_max) + '</s>' : '') + '</span></a>').join('') + all
          : '<div class="apr-sug-none">No shades match \u201c' + esc(q) + '\u201d</div>';
        box.hidden = false;
      } catch (e) {}
    };
    inp.addEventListener('input', () => {
      clearTimeout(t);
      t = setTimeout(run, 180);
    });
    inp.addEventListener('focus', () => {
      if (box.innerHTML && inp.value.trim()) box.hidden = false;
    });
    inp.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') box.hidden = true;
    });
    document.addEventListener('click', (e) => {
      if (!form.contains(e.target)) box.hidden = true;
    });
  }
  // SEO: make each key page's main heading (H1) say what the page is about. The extra words are
  // visually hidden (screen readers and Google read them), so nothing changes on screen.
  function seoH1() {
    const p = location.pathname.replace(/\/$/, '') || '/';
    let add = '';
    let make = '';
    if (p === '/') add = ' – coloured contact lenses for dark eyes in Pakistan';
    else if (/^\/products\//.test(p) && p.indexOf('solution') < 0) add = ' coloured contact lenses';
    else if (/^\/collections\/(browns|blues|greens|greys)$/.test(p)) add = ' – coloured contact lenses for dark eyes';
    else if (/^\/collections\/(shop|all)$/.test(p)) make = 'Coloured contact lenses in Pakistan';
    else if (p === '/pages/bundle-save') add = ' – coloured lens bundles';
    else if (p === '/pages/virtual-try-on') add = ' for coloured contact lenses';
    else if (p === '/pages/faq') add = ' about coloured contact lenses';
    const hs = [...document.querySelectorAll('h1')];
    const shown = hs.filter((h) => h.offsetWidth > 1 && h.offsetHeight > 1);
    if (shown.length) {
      hs.filter((h) => shown.indexOf(h) < 0).forEach((h) => {
        const h2 = document.createElement('h2');
        h2.className = h.className;
        h2.innerHTML = h.innerHTML;
        h.replaceWith(h2);
      });
    }
    const h1 = shown[0];
    if (h1 && add && !h1.querySelector('.apr-vh')) {
      const s = document.createElement('span');
      s.className = 'apr-vh';
      s.textContent = add;
      h1.appendChild(s);
    } else if (!h1 && make) {
      const m = document.querySelector('main') || document.body;
      const n = document.createElement('h1');
      n.className = 'apr-vh';
      n.textContent = make;
      m.prepend(n);
    }
  }
  // Start: draw every april. section and header on the page.
  window.APRIL = { mount, header, cartAdd };
  document.querySelectorAll('.apr[data-view]').forEach(mount);
  document.querySelectorAll('.apr-header').forEach(header);
  searchSuggest();
  seoH1();
/* april bag page (cart) */
function aprBag(){
  if(!/^\/cart\/?$/.test(location.pathname))return;
  var cp=document.querySelector('.cart-page');if(!cp)return;
  var SOL='april-multi-purpose-contact-lens-solution',SAVE={2:600,3:1500,4:2400};
  var root=document.createElement('div');root.id='aprBag';root.className='apr-bag';
  cp.parentElement.insertBefore(root,cp);document.body.classList.add('apr-bag-on');
  var prod={},busy=false;
  function rs(c){return 'Rs.'+Math.round(c/100).toLocaleString('en-US')}
  function esc(s){return String(s==null?'':s).replace(/[&<>"]/g,function(m){return{'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[m]})}
  function save(n){return n>=5?600*n:(SAVE[n]||0)}
  function getP(h){if(prod[h])return Promise.resolve(prod[h]);return fetch('/products/'+h+'.js').then(function(r){return r.ok?r.json():null}).then(function(p){prod[h]=p;return p}).catch(function(){return null})}
  function api(u,b){busy=true;root.classList.add('is-busy');return fetch(u,{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify(b)}).then(function(r){return r.json()}).then(function(){return load()}).finally(function(){busy=false;root.classList.remove('is-busy')})}
  function load(){return fetch('/cart.js').then(function(r){return r.json()}).then(function(c){var hs=[SOL];c.items.forEach(function(i){if(hs.indexOf(i.handle)<0)hs.push(i.handle)});return Promise.all(hs.map(getP)).then(function(){draw(c)})})}
  function draw(c){
    document.querySelectorAll('.apr-header .cnt').forEach(function(e){e.textContent=c.item_count});
    if(!c.item_count){root.innerHTML='<div class="ab-empty"><h1>Your bag is empty</h1><p>Find a shade you love. Every pair comes with solution and a case.</p><a class="ab-cta" href="/collections/shop">Shop all shades</a></div>';return}
    var pairs=0,lensOrig=0,hasSol=false;
    c.items.forEach(function(i){if(i.handle===SOL)hasSol=true;else{pairs+=i.quantity;lensOrig+=i.original_line_price}});
    var unit=pairs?lensOrig/pairs:0,html='<div class="ab-main"><h1>Your bag <small>'+pairs+' pair'+(pairs==1?'':'s')+'</small></h1>';
    if(pairs){
      var msg;
      if(pairs<4){var n=pairs+1;msg='Add <b>1 more pair</b> and pay <b>'+rs(unit*n-save(n)*100)+'</b> for '+n+' — you save <b>'+rs(save(n)*100)+'</b>.'}
      else msg='Nice — you’re saving <b>'+rs(save(pairs)*100)+'</b> on '+pairs+' pairs.';
      html+='<div class="ab-card ab-bundle"><div>'+msg+'</div><div class="ab-steps">';
      for(var s=1;s<=4;s++)html+='<div class="'+(pairs>=s?'on':'')+'"></div>';
      html+='</div><div class="ab-steplab"><span class="'+(pairs==1?'on':'')+'">1 pair</span>';
      for(s=2;s<=4;s++)html+='<span class="'+((pairs==s||(s==4&&pairs>4))?'on':'')+'">'+s+' · save '+rs(SAVE[s]*100)+'</span>';
      html+='</div><a class="ab-link" href="/collections/shop">Add another shade →</a></div>';
    }
    c.items.forEach(function(i){
      var p=prod[i.handle],v=p&&p.variants.filter(function(x){return x.id==i.variant_id})[0],cmp=v&&v.compare_at_price>i.original_price?v.compare_at_price*i.quantity:0;
      var fam=p&&p.type?p.type.replace(/s$/,''):'',meta=i.handle===SOL?'60 ml multi-purpose solution':(fam?esc(fam)+' · ':'')+'zero power · includes 60 ml solution + case';
      var img=i.image?i.image.replace(/(\.[a-z]+)(\?|$)/i,'_240x240$1$2'):'';
      html+='<div class="ab-card ab-item" data-key="'+esc(i.key)+'"><a href="'+esc(i.url)+'"><img src="'+esc(img)+'" alt="'+esc(i.product_title)+'" width="104" height="104" loading="lazy"></a><div class="ab-info"><a class="ab-name" href="'+esc(i.url)+'">'+esc(i.product_title)+'</a><div class="ab-meta">'+meta+'</div><div class="ab-row"><div class="ab-qty"><button type="button" data-q="'+(i.quantity-1)+'" aria-label="Decrease quantity">−</button><span>'+i.quantity+'</span><button type="button" data-q="'+(i.quantity+1)+'" aria-label="Increase quantity">+</button></div><button type="button" class="ab-rm" data-q="0">Remove</button></div></div><div class="ab-price"><b>'+rs(i.final_line_price)+'</b>'+(cmp?'<s>'+rs(cmp)+'</s>':(i.final_line_price<i.original_line_price?'<s>'+rs(i.original_line_price)+'</s>':''))+'</div></div>';
    });
    var sp=prod[SOL],sv=sp&&sp.variants.filter(function(x){return x.available})[0];
    if(sv&&!hasSol)html+='<div class="ab-card ab-up"><img src="'+esc((sp.featured_image||'').replace(/(\.[a-z]+)(\?|$)/i,'_120x120$1$2'))+'" alt="" width="48" height="48" loading="lazy"><div><div class="ab-name">Extra lens solution, 60 ml</div><div class="ab-meta">'+rs(sv.price)+' · a spare bottle for travel or refills</div></div><button type="button" class="ab-btn-o" data-add="'+sv.id+'">Add</button></div>';
    html+='</div>';
    var codes=(c.discount_codes||[]),codeHtml=codes.map(function(d){return '<span class="ab-code'+(d.applicable?'':' bad')+'">'+esc(d.code)+(d.applicable?'':' · not valid for this bag')+' <button type="button" data-rmcode="1" aria-label="Remove code">×</button></span>'}).join('');
    html+='<aside class="ab-card ab-sum"><h2>Order summary</h2><div class="ab-l"><span>Subtotal ('+c.item_count+' item'+(c.item_count==1?'':'s')+')</span><span>'+rs(c.original_total_price)+'</span></div><div class="ab-l"><span>Savings</span><span>'+(c.total_discount?'−'+rs(c.total_discount):'Rs.0')+'</span></div><div class="ab-l"><span>Delivery</span><span class="ab-free">FREE</span></div><div class="ab-l ab-tot"><span>Total</span><span>'+rs(c.total_price)+'</span></div><a class="ab-cta" href="/checkout">Checkout</a><p class="ab-cod">Pay cash on delivery · no advance payment</p><div class="ab-trust"><div><b>Free delivery</b>all over Pakistan</div><div><b>Cash on delivery</b>pay at your door</div><div><b>3–5 days</b>after 1–2 days to pack</div></div><details class="ab-disc"'+(codes.length?' open':'')+'><summary>Have a discount code? <span>+</span></summary><form class="ab-dform"><input name="code" placeholder="Discount code" autocomplete="off" aria-label="Discount code"><button type="submit" class="ab-btn-o">Apply</button></form>'+codeHtml+'</details><a class="ab-cont" href="/collections/shop">Continue shopping</a></aside>';
    html+='<div class="ab-stick"><div><div class="ab-tt">Total · free delivery</div><div class="ab-tv">'+rs(c.total_price)+'</div></div><a class="ab-cta" href="/checkout">Checkout · Cash on delivery</a></div>';
    root.innerHTML=html;root._codes=codes.map(function(d){return d.code});
  }
  root.addEventListener('click',function(e){
    if(busy)return;var b=e.target.closest('button');if(!b)return;
    var it=b.closest('.ab-item');
    if(it&&b.hasAttribute('data-q')){e.preventDefault();api('/cart/change.js',{id:it.getAttribute('data-key'),quantity:Math.max(0,+b.getAttribute('data-q'))})}
    else if(b.hasAttribute('data-add')){e.preventDefault();api('/cart/add.js',{items:[{id:+b.getAttribute('data-add'),quantity:1}]})}
    else if(b.hasAttribute('data-rmcode')){e.preventDefault();api('/cart/update.js',{discount:''})}
  });
  root.addEventListener('submit',function(e){
    var f=e.target.closest('.ab-dform');if(!f)return;e.preventDefault();if(busy)return;
    var v=f.code.value.trim();if(!v)return;
    var list=(root._codes||[]).filter(function(x){return x.toLowerCase()!==v.toLowerCase()});list.push(v);
    api('/cart/update.js',{discount:list.join(',')});
  });
  root.innerHTML='<div class="ab-load">Loading your bag…</div>';
  load().catch(function(){document.body.classList.remove('apr-bag-on');root.remove()});
}
aprBag();
/* product page: Proceed to checkout button under Add to bag */
function aprGo(){
  if(!/^\/products\//.test(location.pathname))return;
  function place(){
    document.querySelectorAll('.mp-buy').forEach(function(buy){
      if(buy.nextElementSibling&&buy.nextElementSibling.classList.contains('mp-go'))return;
      var add=buy.querySelector('.mp-add');if(!add)return;
      var b=document.createElement('button');b.type='button';b.className='mp-go';b.textContent='Proceed to checkout';
      b.setAttribute('data-h',add.getAttribute('data-addq')||location.pathname.split('/')[2]);
      buy.insertAdjacentElement('afterend',b);
    });
  }
  document.addEventListener('click',function(e){
    var b=e.target.closest('.mp-go');if(!b||b.disabled)return;e.preventDefault();
    var q=parseInt((document.getElementById('qo')||{}).textContent,10)||1,t='Proceed to checkout';
    b.disabled=true;b.textContent='Taking you to checkout…';
    fetch('/products/'+b.getAttribute('data-h')+'.js').then(function(r){return r.json()}).then(function(p){
      var v=p.variants.filter(function(x){return x.available})[0];if(!v)throw 0;
      return fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items:[{id:v.id,quantity:q}]})});
    }).then(function(r){if(!r.ok)throw 0;location.href='/checkout'}).catch(function(){b.textContent='Sorry, this shade could not be added';setTimeout(function(){b.disabled=false;b.textContent=t},2500)});
  });
  place();new MutationObserver(place).observe(document.body,{childList:true,subtree:true});
}
aprGo();
/* try-on: live camera zooms in on the face so the eyes are close and clear */
function vtZoom(){
  var z=1,cx=0.5,cy=0.5;
  function tick(){
    var cv=document.getElementById('vtoC');
    if(cv){
      var tz=1,tcx=0.5,tcy=0.5,e=VT.eyes,img=VT.img;
      if(VT.mode==='live'&&VT.live&&e&&e.length>=2&&img&&img.width){
        var W=img.width,H=img.height,a=e[0],b=e[1];
        var d=Math.hypot(a.cx-b.cx,a.cy-b.cy);
        if(d>4){
          tz=Math.max(1,Math.min(2.4,W/(2.9*d),H/(3.8*d)));
          tcx=((a.cx+b.cx)/2)/W;
          tcy=((a.cy+b.cy)/2)/H;
        }
      }
      z+=(tz-z)*0.12;cx+=(tcx-cx)*0.12;cy+=(tcy-cy)*0.12;
      if(Math.abs(z-1)<0.005&&tz===1){cv.style.transform='';cv.style.transformOrigin='';}
      else{
        var tx=Math.max(1-z,Math.min(0,0.5-cx*z)),ty=Math.max(1-z,Math.min(0,0.5-cy*z));
        cv.style.transformOrigin='0 0';
        cv.style.transform='translate('+(tx*100).toFixed(2)+'%,'+(ty*100).toFixed(2)+'%) scale('+z.toFixed(3)+')';
      }
    }
    requestAnimationFrame(tick);
  }
  if(document.getElementById('vtoStage'))requestAnimationFrame(tick);
  else{var n=0,iv=setInterval(function(){if(document.getElementById('vtoStage')){clearInterval(iv);requestAnimationFrame(tick)}else if(++n>40)clearInterval(iv)},250)}
}
vtZoom();
})();

/* april. phone bottom bar (Home / Shop / Try on / Bag) - shows after scrolling down */
function aprNav(){
  var p=location.pathname;
  if(/^\/(checkout|password)/.test(p))return;
  if(document.getElementById('aprNav'))return;
  var I={
    home:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V20h5v-6h4v6h5V9.5"/></svg>',
    shop:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3.5" y="3.5" width="7" height="7" rx="2"/><rect x="13.5" y="3.5" width="7" height="7" rx="2"/><rect x="3.5" y="13.5" width="7" height="7" rx="2"/><rect x="13.5" y="13.5" width="7" height="7" rx="2"/></svg>',
    eye:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z"/><circle cx="12" cy="12" r="3.2"/></svg>',
    bag:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8h14l-1.2 12.2H6.2Z"/><path d="M9 8V6.5a3 3 0 0 1 6 0V8"/></svg>'
  };
  var L=[['/','Home','home',p==='/'],['/collections/shop','Shop','shop',/^\/collections/.test(p)],['/pages/virtual-try-on','Try on','eye',/virtual-try-on/.test(p)],['/cart','Bag','bag',false]];
  var n=document.createElement('nav');n.id='aprNav';n.className='apr-nav';n.setAttribute('aria-label','Quick links');
  n.innerHTML=L.map(function(x){return '<a href="'+x[0]+'"'+(x[3]?' class="cur" aria-current="page"':'')+'>'+I[x[2]]+'<span>'+x[1]+'</span>'+(x[2]==='bag'?'<b class="bd" data-z="1"></b>':'')+'</a>'}).join('');
  document.body.appendChild(n);
  var bd=n.querySelector('.bd');
  function setN(c){c=parseInt(c,10)||0;bd.textContent=c>99?'99+':c;if(c)bd.removeAttribute('data-z');else bd.setAttribute('data-z','1')}
  function fromHeader(){var e=document.querySelector('.apr-header .cnt');if(e)setN(e.textContent);return !!e}
  if(!fromHeader())fetch('/cart.js').then(function(r){return r.json()}).then(function(c){setN(c.item_count)}).catch(function(){});
  var hd=document.querySelector('.apr-header');if(hd)new MutationObserver(fromHeader).observe(hd,{childList:true,subtree:true,characterData:true});
  var on=false,last=0;
  function top(){var w=document.querySelector('.page-wrapper');return Math.max(window.scrollY||0,document.documentElement.scrollTop||0,w?w.scrollTop:0)}
  function bar(){var b=document.querySelector('.apr-bag .ab-stick,.apr .mbar');if(!b)return 0;var r=b.getBoundingClientRect(),cs=getComputedStyle(b);return cs.display!=='none'&&cs.position==='fixed'&&r.height&&r.bottom>=innerHeight-2?r.height:0}
  function place(){var o=bar();n.style.bottom=o?(o+10)+'px':'';document.body.style.setProperty('--aprNavLift',((o?o+10:12)+n.offsetHeight+14)+'px')}
  function set(s){if(s!==on){on=s;if(s)place();n.classList.toggle('on',s);document.body.classList.toggle('apr-nav-on',s)}}
  function chk(){var t=top(),d=t-last;if(Math.abs(d)<6)return;last=t;set(d<0)}
  document.addEventListener('scroll',chk,{passive:true,capture:true});addEventListener('scroll',chk,{passive:true});addEventListener('resize',function(){if(on)place()});
}
aprNav();

/* phones: open a product card on the first tap (no hover step) */
function aprTap(){
  var s=null;
  document.addEventListener('touchstart',function(e){var a=e.target.closest&&e.target.closest('a.pc,#aprNav a');s=a&&e.touches.length===1?{a:a,x:e.touches[0].clientX,y:e.touches[0].clientY,t:Date.now()}:null},{passive:true});
  document.addEventListener('touchend',function(e){if(!s)return;var t=e.changedTouches[0],a=s.a,ok=Math.abs(t.clientX-s.x)<10&&Math.abs(t.clientY-s.y)<10&&Date.now()-s.t<600;s=null;if(ok&&a.href&&a.contains(e.target)){e.preventDefault();location.href=a.href}},{passive:false});
}
aprTap();

/* bag page: Continue shopping link at the top */
function aprBagBack(){
  if(!/^\/cart\/?$/.test(location.pathname))return;
  function put(){var h=document.querySelector('#aprBag .ab-main h1');if(h&&!document.querySelector('#aprBag .ab-back')){var a=document.createElement('a');a.className='ab-back';a.href='/collections/shop';a.innerHTML='<span aria-hidden="true">←</span> Continue shopping';h.parentNode.insertBefore(a,h)}}
  put();new MutationObserver(put).observe(document.body,{childList:true,subtree:true});
}
aprBagBack();

/* bundle page: no bottom Add-to-bag bar */
(function(){
  if(!/^\/pages\/bundle-save/.test(location.pathname))return;
  function rm(){var m=document.getElementById('mbar');if(m&&m.style.display!=='none')m.style.setProperty('display','none','important');if(document.body.classList.contains('has-mbar'))document.body.classList.remove('has-mbar')}
  rm();new MutationObserver(rm).observe(document.body,{attributes:true,attributeFilter:['class'],childList:true});
})();

/* bottom bar: open on the first tap on iPhone (keep the bar in place while it is being tapped) */
(function(){
  var n=document.getElementById('aprNav');if(!n)return;
  var g=null,tm=0;
  n.addEventListener('touchstart',function(e){var a=e.target.closest&&e.target.closest('a');if(!a||e.touches.length!==1){g=null;return}g={a:a,x:e.touches[0].clientX,y:e.touches[0].clientY};clearTimeout(tm);n.classList.add('hold')},{passive:true});
  n.addEventListener('touchend',function(e){var x=g;g=null;tm=setTimeout(function(){n.classList.remove('hold')},1200);if(!x)return;var t=e.changedTouches[0];if(Math.abs(t.clientX-x.x)<14&&Math.abs(t.clientY-x.y)<14&&x.a.href){e.preventDefault();e.stopPropagation();location.href=x.a.href}},{passive:false});
  n.addEventListener('touchcancel',function(){g=null;tm=setTimeout(function(){n.classList.remove('hold')},600)});
})();

/* ===== april. free automations (added Oct 2026): bag reminder, delivery estimate, low stock, newsletter, recently viewed, notify me ===== */
(function(){
  var P=location.pathname, LS={g:function(k){try{return localStorage.getItem(k)}catch(e){return null}},s:function(k,v){try{localStorage.setItem(k,v)}catch(e){}}},
      SS={g:function(k){try{return sessionStorage.getItem(k)}catch(e){return null}},s:function(k,v){try{sessionStorage.setItem(k,v)}catch(e){}}};
  if(/^\/(checkout|password|account|challenge)/.test(P))return;
  var D=window.APRIL_DATA||{products:[]}, BY={};(D.products||[]).forEach(function(p){BY[p.h]=p});
  var isProd=/^\/products\//.test(P), handle=isProd?decodeURIComponent(P.split('/')[2]||''):'';
  function esc(s){return String(s==null?'':s).replace(/[&<>"]/g,function(c){return{'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c]})}
  function rs(n){return 'Rs.'+Math.round(n).toLocaleString('en-US')}
  function sz(u,w){return String(u||'').replace(/width=\d+/,'width='+w)}
  function post(fields){ // normal Shopify form submit (Shopify blocks background posts); the page comes back with ?customer_posted=true or ?contact_posted=true
    var f=document.createElement('form');f.method='post';f.action='/contact#contact_form';f.acceptCharset='UTF-8';fields.utf8='\u2713';
    Object.keys(fields).forEach(function(k){var i=document.createElement('input');i.type='hidden';i.name=k;i.value=fields[k];f.appendChild(i)});
    document.body.appendChild(f);if(f.requestSubmit)f.requestSubmit();else f.submit();return new Promise(function(){});
  }

  /* 1. Your bag is waiting (returning visitors, once per visit) */
  if(!/^\/cart/.test(P)&&!SS.g('aprBagNudge')){
    SS.s('aprBagNudge','1');
    fetch('/cart.js').then(function(r){return r.json()}).then(function(c){
      if(!c||!c.item_count||!LS.g('aprSeen'))return;
      var h=document.querySelector('.apr-header');var host=h&&(h.closest('.shopify-section')||h);if(!host)return;
      var b=document.createElement('div');b.className='apr-bagnudge';b.setAttribute('role','status');
      b.innerHTML='<span><b>Your bag is waiting</b> · '+c.item_count+' item'+(c.item_count==1?'':'s')+' · '+rs(c.total_price/100)+'</span><a href="/cart" class="v">View bag</a><a href="/checkout" class="c">Checkout</a><button type="button" aria-label="Close">×</button>';
      host.insertAdjacentElement('afterend',b);
      b.querySelector('button').addEventListener('click',function(){b.remove()});
    }).catch(function(){});
  }
  LS.s('aprSeen','1');

  /* 2, 3, 6: product page extras under Add to bag */
  var STOCK_LIMIT=5; // show "Only X left" at or below this number
  function addDays(n){var d=new Date();d.setDate(d.getDate()+n);return d.toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'short'})}
  function prodExtras(){
    document.querySelectorAll('.mp-buy').forEach(function(buy){
      if(buy.parentElement.querySelector('.apr-px'))return;
      var add=buy.querySelector('.mp-add'),sold=add&&add.disabled,h=(add&&add.getAttribute('data-addq'))||handle,p=BY[h]||{};
      var box=document.createElement('div');box.className='apr-px';var html='';
      var q=window.APRIL_STOCK;
      if(!sold&&typeof q==='number'&&q>0&&q<=STOCK_LIMIT)html+='<p class="px-low"><i></i>Only '+q+' left in stock</p>';
      if(!sold)html+='<p class="px-del"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7"/><circle cx="7" cy="17.5" r="1.5"/><circle cx="17" cy="17.5" r="1.5"/></svg><span>Order today, delivered between <b>'+addDays(4)+'</b> and <b>'+addDays(7)+'</b><small>Free delivery · Cash on delivery</small></span></p>';
      if(sold)html+='<form class="px-notify" method="post" action="/contact#contact_form" accept-charset="UTF-8"><input type="hidden" name="form_type" value="contact"><input type="hidden" name="utf8" value="\u2713"><input type="hidden" name="contact[name]" value="Back-in-stock request"><input type="hidden" name="contact[body]" value=""><b>Get told when it’s back</b><span>Leave your email and we’ll let you know when '+esc(p.n||'this shade')+' is back in stock.</span><div><input type="email" name="contact[email]" required placeholder="Your email" aria-label="Your email"><button type="submit">Notify me</button></div><input type="tel" name="wa" placeholder="WhatsApp number (optional)" aria-label="WhatsApp number (optional)"><em role="status"></em></form>';
      if(!html)return;
      box.innerHTML=html;buy.insertAdjacentElement('afterend',box);
      var f=box.querySelector('.px-notify');
      if(f)f.addEventListener('submit',function(e){
        e.preventDefault();var m=f.querySelector('em'),bt=f.querySelector('button');bt.disabled=true;m.textContent='Sending…';
        post({form_type:'contact','contact[email]':f.email.value,'contact[name]':'Back-in-stock request','contact[body]':'Please tell me when '+(p.n||h)+' is back in stock.'+(f.wa.value?'\nWhatsApp: '+f.wa.value:'')+'\nPage: '+location.href})
          .then(function(r){if(r==='ok'){m.textContent='Done! We’ll let you know when it’s back.';f.querySelector('div').style.display='none';f.wa.style.display='none'}})
          .catch(function(){bt.disabled=false;m.textContent='Could not send. Please try again or message us on WhatsApp.'});
      });
    });
  }

  /* 5. Recently viewed shades (product pages) */
  var RV=[];try{RV=JSON.parse(LS.g('aprRV')||'[]')}catch(e){}
  if(isProd&&BY[handle]){RV=[handle].concat(RV.filter(function(x){return x!==handle})).slice(0,8);LS.s('aprRV',JSON.stringify(RV))}
  function recent(){
    if(!isProd||document.querySelector('.apr-rv'))return;
    var app=document.querySelector('.apr-app');if(!app||!app.querySelector('.mp-top'))return;
    var list=RV.filter(function(x){return x!==handle&&BY[x]}).slice(0,8);if(!list.length)return;
    var s=document.createElement('section');s.className='apr-rv';
    var AR=function(d){return '<button class="ra '+(d<0?'l':'r')+'" data-dir="'+d+'" aria-label="Scroll '+(d<0?'left':'right')+'"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="'+(d<0?'m15 6-6 6 6 6':'m9 6 6 6-6 6')+'"/></svg></button>'};
    s.innerHTML='<div class="wrap"><h2 class="sec-t">Your shade search</h2><div class="rail-w">'+AR(-1)+'<div class="rail">'+list.map(function(x){var p=BY[x];return '<a class="pc" href="/products/'+esc(x)+'"><div class="im"><img class="a" src="'+esc(sz(p.imgs[0],500))+'" alt="'+esc(p.n)+'" loading="lazy" width="500" height="500">'+(p.imgs[1]?'<img class="b" src="'+esc(sz(p.imgs[1],400))+'" alt="'+esc(p.n)+' lens" loading="lazy" width="400" height="400">':'')+'</div><div class="nm">'+esc(p.n)+'</div><div class="pr">'+(p.was>p.price?rs(p.price)+'<s>'+rs(p.was)+'</s>':rs(p.price))+'</div></a>'}).join('')+'</div>'+AR(1)+'</div></div>';
    var ln=app.querySelector('.mp-line');if(ln)ln.insertAdjacentElement('beforebegin',s);else app.appendChild(s);
    var el=s.querySelector('.rail'),ars=s.querySelectorAll('.ra');
    function upd(){var mx=el.scrollWidth-el.clientWidth;ars[0].disabled=el.scrollLeft<=2;ars[1].disabled=el.scrollLeft>=mx-2}
    ars.forEach(function(a){a.addEventListener('click',function(){var c=el.querySelector('.pc');el.scrollBy({left:+a.getAttribute('data-dir')*(c?c.getBoundingClientRect().width+18:260)*2,behavior:'smooth'})})});
    el.addEventListener('scroll',upd,{passive:true});upd();setTimeout(upd,600);
  }

  /* 4. Newsletter popup (once per visitor) */
  function nl(){
    if(LS.g('aprNl')||/^\/cart/.test(P)||/customer_posted/.test(location.search))return;
    var shown=false;
    function show(){
      if(shown||document.querySelector('.apr-nl'))return;shown=true;LS.s('aprNl','1');
      var w=document.createElement('div');w.className='apr-nl';w.setAttribute('role','dialog');w.setAttribute('aria-label','Join the april. list');
      w.innerHTML='<div class="nl-c"><button type="button" class="x" aria-label="Close">×</button><img src="/cdn/shop/files/april-logo-pink.png?width=240" alt="april." width="120"><h3>Be the first to know</h3><p>New shades, offers and lens-care tips. No spam, unsubscribe anytime.</p><form method="post" action="/contact#contact_form" accept-charset="UTF-8"><input type="hidden" name="form_type" value="customer"><input type="hidden" name="utf8" value="\u2713"><input type="hidden" name="contact[tags]" value="newsletter,popup"><input type="email" name="contact[email]" required placeholder="Your email" aria-label="Your email"><button type="submit">Join</button></form><em role="status"></em></div>';
      document.body.appendChild(w);requestAnimationFrame(function(){w.classList.add('in')});
      function close(){w.classList.remove('in');setTimeout(function(){w.remove()},300)}
      w.addEventListener('click',function(e){if(e.target===w||e.target.closest('.x'))close()});
      document.addEventListener('keydown',function(e){if(e.key==='Escape')close()});
      var f=w.querySelector('form'),m=w.querySelector('em');
      f.addEventListener('submit',function(){m.textContent='Joining\u2026';try{localStorage.setItem('aprNl','1')}catch(x){}});if(0)f.addEventListener('submit',function(e){e.preventDefault();var b=f.querySelector('button');b.disabled=true;m.textContent='Joining…';
        post({form_type:'customer','contact[email]':f.email.value,'contact[tags]':'newsletter,popup'})
          .then(function(r){if(r==='ok'){f.style.display='none';m.textContent='You’re on the list. Thank you!';setTimeout(close,2600)}})
          .catch(function(){b.disabled=false;m.textContent='Could not join. Please try again.'})});
    }
    setTimeout(show,15000);
    document.addEventListener('mouseout',function(e){if(!e.relatedTarget&&e.clientY<=0)show()});
  }

  function run(){prodExtras();recent()}
  run();if(isProd)new MutationObserver(run).observe(document.body,{childList:true,subtree:true});
  nl();
})();

/* footer: Facebook link, no TikTok icon, pink logo */
(function(){
  function fx(){
    document.querySelectorAll('.social-links a[aria-label="TikTok"]').forEach(function(a){a.remove()});
    document.querySelectorAll('.social-links a[aria-label="Facebook"]').forEach(function(a){a.href='https://www.facebook.com/share/1F6tq2XZem/?mibextid=wwXIfr';a.target='_blank';a.rel='noopener'});
    document.querySelectorAll('img.footer-logo').forEach(function(i){if(/april-logo-black/.test(i.src))i.src=i.src.replace('april-logo-black','april-logo-pink')});
  }
  fx();if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',fx);
})();

/* thank-you message after the newsletter or Notify me form is sent */
(function(){
  var q=location.search;if(!/(customer_posted|contact_posted)=true/.test(q))return;
  try{localStorage.setItem('aprNl','1')}catch(e){}
  var m=/customer_posted/.test(q)?'You\u2019re on the list. Thank you!':'Thank you! We\u2019ll let you know when it\u2019s back.';
  function show(){var t=document.createElement('div');t.className='apr-ok';t.setAttribute('role','status');t.textContent=m;document.body.appendChild(t);setTimeout(function(){t.classList.add('in')},60);setTimeout(function(){t.classList.remove('in');setTimeout(function(){t.remove()},400)},5000)}
  if(document.body)show();else document.addEventListener('DOMContentLoaded',show);
})();

/* Save my bag to WhatsApp (bag page) */
(function(){
  if(!/^\/cart\/?$/.test(location.pathname))return;
  var WA='923319576192';
  var q=new URLSearchParams(location.search).get('restore');
  if(q){
    var want={};
    q.split(',').forEach(function(p){var a=p.split(':'),id=parseInt(a[0],10),n=Math.min(10,Math.max(1,parseInt(a[1],10)||1));if(id>0)want[id]=n});
    if(Object.keys(want).length){
      document.documentElement.classList.add('apr-restoring');
      fetch('/cart.js',{headers:{Accept:'application/json'}}).then(function(r){return r.json()}).then(function(c){
        var have={},up={};
        c.items.forEach(function(i){have[i.variant_id]=(have[i.variant_id]||0)+i.quantity});
        Object.keys(want).forEach(function(id){if((have[id]||0)<want[id])up[id]=want[id]});
        if(!Object.keys(up).length)return;
        return fetch('/cart/update.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({updates:up})});
      }).catch(function(){}).then(function(){location.replace('/cart')});
      return;
    }
  }
  function rs(c){return 'Rs.'+Math.round(c/100).toLocaleString('en-US')}
  function link(c){
    var items=c.items.filter(function(i){return i.quantity>0});
    var lines=items.map(function(i){return '\u2022 '+i.product_title+' \u00d7 '+i.quantity});
    var back=location.origin+'/cart?restore='+items.map(function(i){return i.variant_id+':'+i.quantity}).join(',');
    var msg='Hi april., please save my bag:\n'+lines.join('\n')+'\nTotal: '+rs(c.total_price)+' (free delivery, cash on delivery)\nMy bag: '+back;
    return 'https://wa.me/'+WA+'?text='+encodeURIComponent(msg);
  }
  var IC='<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 11.5a8 8 0 0 1-11.8 7L4 20l1.5-4.1A8 8 0 1 1 20 11.5Z"/></svg>';
  function add(){
    var r=document.getElementById('aprBag');if(!r)return;
    var sum=r.querySelector('.ab-sum');if(!sum||sum.querySelector('.apr-wa'))return;
    var a=document.createElement('a');a.className='apr-wa';a.target='_blank';a.rel='noopener';
    a.href='https://wa.me/'+WA;a.innerHTML=IC+'<span>Save my bag on WhatsApp</span>';
    var h=document.createElement('p');h.className='apr-wa-h';h.textContent='Sends your bag to april. on WhatsApp, with a link to come back to it anytime.';
    var at=sum.querySelector('.ab-cod')||sum.querySelector('.ab-cta');
    if(at){at.insertAdjacentElement('afterend',h);at.insertAdjacentElement('afterend',a)}else{sum.appendChild(a);sum.appendChild(h)}
    fetch('/cart.js',{headers:{Accept:'application/json'}}).then(function(x){return x.json()}).then(function(c){if(c.item_count)a.href=link(c);else{a.remove();h.remove()}}).catch(function(){});
  }
  new MutationObserver(add).observe(document.documentElement,{childList:true,subtree:true});
  add();
})();


/* Home journal row: auto-moves, and can be swiped, dragged and tapped */
(function(){
  var reduce=window.matchMedia&&matchMedia('(prefers-reduced-motion: reduce)').matches;
  function setup(m){
    if(m.getAttribute('data-apr-drag'))return;
    var t=m.querySelector('.jmx-t');if(!t||t.children.length<2)return;
    m.setAttribute('data-apr-drag','1');
    document.documentElement.classList.add('apr-jmx-js');
    var kids=t.children,n=Math.floor(kids.length/2),SPEED=0.03,pos=1,set=-1,resume=0,hover=false,down=null,moved=false,last=0,seen=true;
    function loop(){var a=kids[0],b=kids[n];return (a&&b)?b.offsetLeft-a.offsetLeft:t.scrollWidth/2}
    function wrap(){var L=loop();if(L<50)return;if(m.scrollLeft>=L)m.scrollLeft-=L;else if(m.scrollLeft<1)m.scrollLeft+=L}
    t.querySelectorAll('img').forEach(function(i){i.setAttribute('draggable','false')});
    m.scrollLeft=pos;set=m.scrollLeft;
    function tick(ts){
      if(!m.isConnected)return;
      var dt=last?Math.min(ts-last,100):0;last=ts;
      if(Math.abs(m.scrollLeft-set)>2){pos=m.scrollLeft;resume=Date.now()+2500}
      if(!reduce&&seen&&!hover&&!down&&Date.now()>resume){pos+=SPEED*dt}else{pos=m.scrollLeft}
      var L=loop();if(L>50){if(pos>=L)pos-=L;else if(pos<1)pos+=L}
      m.scrollLeft=pos;set=m.scrollLeft;
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
    if('IntersectionObserver' in window)new IntersectionObserver(function(e){seen=e[0].isIntersecting}).observe(m);
    m.addEventListener('mouseenter',function(){hover=true});
    m.addEventListener('mouseleave',function(){hover=false});
    m.addEventListener('touchstart',function(){resume=Date.now()+2500;hover=false},{passive:true});
    m.addEventListener('touchend',function(){resume=Date.now()+2500},{passive:true});
    m.addEventListener('focusin',function(){resume=Date.now()+4000});
    m.addEventListener('pointerdown',function(e){if(e.pointerType!=='mouse'||e.button!==0)return;down={x:e.clientX,s:m.scrollLeft};moved=false});
    window.addEventListener('pointermove',function(e){if(!down)return;var dx=e.clientX-down.x;if(Math.abs(dx)>5){moved=true;m.classList.add('is-drag')}if(moved){m.scrollLeft=down.s-dx;wrap();down.s=m.scrollLeft+dx}});
    window.addEventListener('pointerup',function(){if(!down)return;down=null;m.classList.remove('is-drag');resume=Date.now()+1500});
    m.addEventListener('click',function(e){if(moved){e.preventDefault();e.stopPropagation();moved=false}},true);
    m.addEventListener('dragstart',function(e){e.preventDefault()});
  }
  function scan(){document.querySelectorAll('.jmx-m:not([data-apr-drag])').forEach(setup)}
  new MutationObserver(scan).observe(document.documentElement,{childList:true,subtree:true});
  scan();
})();
